import logger from '../../lib/logger';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import heic2any from 'heic2any';
import ScaffoldingProperties from './ScaffoldingProperties';
import TrussProperties from './TrussProperties';
import { 
  Eye, 
  EyeOff, 
  Lock, 
  Unlock, 
  Trash2,
  Projector,
  Box,
  Layers,
  Image,
  Upload,
  Calculator,
  Download,
  Settings,
  Power,
  Grid3X3,
  Frame,
  AlertTriangle,
  Plus,
  FileImage,
  Wand2,
  Copy,
  Volume2,
  VolumeX,
  CheckSquare,
  Square,
  Ruler,
  Edit3,
  X,
  Save,
  Grid2X2,
  BoxSelect,
  Undo2,
  Redo2,
  Check,
  ChevronRight,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Move,
  Maximize2,
  FolderOpen
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import SmartUVExport from './SmartUVExport';
import MultiProjectionPanel from './MultiProjectionPanel';
import { useApp, ASPECT_RATIOS, UNITS, metersToUnit, unitToMeters, formatWithUnit } from '../../context/AppContext';
import { tokenStore } from '../../lib/tokenStore';
import { toast } from 'sonner';
import ContentBrowser from '../ContentBrowser';
import { calculateScaffoldLoad } from '../Editor/ScaffoldingSystem';
import { getProjectorLux, luxColorCSS } from '../../utils/luxCalculation';
import { exportScaffoldReport } from '../Editor/ScaffoldReportExport';
import { exportTrussReport } from '../Editor/TrussReportExport';
import { calculateTrussLoad, TRUSS_PROFILES } from '../Editor/TrussSystem';
import { TRUSS_LIBRARY, JUNCTION_TYPES, CORNER_BLOCKS, BASE_PLATES, validateSpanLength, interpolateLoad, getLoadStatus, getProfileList, getCompatibleCornerBlocks, getCompatibleBasePlates, getProfilesForStructure, getAutoJunctionType, getFilteredCornerBlocks, calculateSections } from '../Editor/TrussData';
import { contentApi } from '../../lib/api';
import LedScreenProperties from './LedScreenProperties';
import CameraPanel from './CameraPanel';

// Common resolution presets (sorted by total pixels for easy comparison)
const RESOLUTION_PRESETS = [
  { name: 'SVGA', width: 800, height: 600 },
  { name: 'XGA', width: 1024, height: 768 },
  { name: 'HD 720p', width: 1280, height: 720 },
  { name: 'WXGA', width: 1280, height: 800 },
  { name: 'SXGA', width: 1280, height: 1024 },
  { name: 'WXGA+', width: 1440, height: 900 },
  { name: 'SXGA+', width: 1400, height: 1050 },
  { name: 'Full HD 1080p', width: 1920, height: 1080 },
  { name: 'WUXGA', width: 1920, height: 1200 },
  { name: 'QHD 1440p', width: 2560, height: 1440 },
  { name: 'WQXGA', width: 2560, height: 1600 },
  { name: '4K UHD', width: 3840, height: 2160 },
  { name: '4K DCI', width: 4096, height: 2160 },
  { name: '5K', width: 5120, height: 2880 },
];

// Get available resolutions up to a max resolution
const getAvailableResolutions = (maxWidth, maxHeight) => {
  return RESOLUTION_PRESETS.filter(r => r.width <= maxWidth && r.height <= maxHeight)
    .sort((a, b) => (a.width * a.height) - (b.width * b.height));
};

// Dimension input component - handles typing multi-digit numbers properly
const DimensionInput = ({ value, onChange, unit, step, label, testId }) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  
  // Convert meters to display unit
  const displayValue = metersToUnit(value || 0, unit || 'm');
  const decimals = unitConfig?.decimals ?? 2;
  
  // Sync local value when not focused
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(displayValue.toFixed(decimals));
    }
  }, [displayValue, isFocused, decimals]);
  
  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(displayValue.toFixed(decimals));
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    const num = parseFloat(localValue);
    if (!isNaN(num) && num >= 0) {
      const metersValue = unitToMeters(num, unit || 'm');
      onChange(metersValue);
    } else {
      setLocalValue(displayValue.toFixed(decimals));
    }
  };
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };
  
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-zinc-500">{label}</Label>
      <Input
        type="number"
        step={step}
        min="0"
        value={isFocused ? localValue : displayValue.toFixed(decimals)}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs font-mono bg-zinc-900"
        data-testid={testId}
      />
    </div>
  );
};

// Throw Distance input - allows typing full numbers before applying
const ThrowDistanceInput = ({ value, onChange, unit }) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  
  const displayValue = metersToUnit(value || 0, unit || 'm');
  const decimals = unitConfig?.decimals ?? 2;
  
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(displayValue.toFixed(decimals));
    }
  }, [displayValue, isFocused, decimals]);
  
  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(displayValue.toFixed(decimals));
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    const num = parseFloat(localValue);
    if (!isNaN(num) && num >= 0) {
      onChange(unitToMeters(num, unit || 'm'));
    } else {
      setLocalValue(displayValue.toFixed(decimals));
    }
  };
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };
  
  return (
    <Input
      type="number"
      min="0"
      max="1000"
      step={unit === 'mm' ? 100 : unit === 'cm' ? 10 : 0.1}
      value={isFocused ? localValue : displayValue.toFixed(decimals)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="w-24 h-6 text-xs font-mono bg-zinc-900 text-right"
      data-testid="throw-distance-input"
    />
  );
};

// Texture Control slider row — hoisted to module scope so React keeps
// component identity stable across parent re-renders (fixes focus loss
// while typing and stuttering during slider drags).
const TextureSliderRow = React.memo(({ label, value, min, max, suffix, field, testId, onCommit }) => {
  const clamp = (v) => Math.max(min, Math.min(max, v));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-zinc-400">{label}</Label>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm"
            className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
            onClick={() => onCommit(field, clamp(Math.round((value - 1) * 100) / 100))}
            data-testid={`${testId}-dec`}
          >−</Button>
          <Input
            type="number"
            value={value}
            min={min}
            max={max}
            step={1}
            className="h-6 w-16 text-xs bg-zinc-900 text-center"
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '-') {
                onCommit(field, raw === '' ? 0 : -0);
                return;
              }
              const v = parseFloat(raw);
              if (!isNaN(v)) onCommit(field, clamp(v));
            }}
            data-testid={`${testId}-input`}
          />
          <span className="text-[10px] text-zinc-500 w-3">{suffix}</span>
          <Button
            variant="ghost" size="sm"
            className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
            onClick={() => onCommit(field, clamp(Math.round((value + 1) * 100) / 100))}
            data-testid={`${testId}-inc`}
          >+</Button>
        </div>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={1}
        onValueChange={([v]) => onCommit(field, v)}
        data-testid={`${testId}-slider`}
      />
    </div>
  );
});
TextureSliderRow.displayName = 'TextureSliderRow';


// Surface Image Size — read-only measurement panel.
// Polls computeSurfaceImageSize() every 250 ms; relies on
// window.__babylonScene + window.__projectorMeshes which BabylonScene
// exposes on mount. Pure measurement: no projection-side state changes.
const SurfaceImageSizePanel = React.memo(({ projector, projectorId, useSurfaceSize, overlayEnabled, projectSettings, onUpdate }) => {
  const [data, setData] = React.useState(null);
  const safeUnit = projectSettings?.unit || 'm';
  const decimals = (projectSettings?.unitConfig?.decimals) ?? 2;

  React.useEffect(() => {
    if (!useSurfaceSize) { setData(null); return undefined; }
    let mounted = true;
    const tick = () => {
      try {
        const scene = (typeof window !== 'undefined') ? window.__babylonScene : null;
        const meshes = (typeof window !== 'undefined') ? window.__projectorMeshes : null;
        if (!scene) return;
        // Lazy-imported so PropertiesPanel never bundles BABYLON itself.
        // eslint-disable-next-line global-require
        const { computeSurfaceImageSize } = require('../../lib/surfaceImageSize');
        const r = computeSurfaceImageSize(projector, scene, { projectorMeshes: meshes });
        if (mounted) setData(r);
      } catch (_) { /* swallow — measurement is best-effort */ }
    };
    tick();
    const t = setInterval(tick, 250);
    return () => { mounted = false; clearInterval(t); };
  }, [useSurfaceSize, projectorId, projector]);

  const fmt = (m) => {
    if (m == null || !isFinite(m)) return '—';
    if (safeUnit === 'm') return `${m.toFixed(decimals)}m`;
    if (safeUnit === 'cm') return `${(m * 100).toFixed(decimals)}cm`;
    if (safeUnit === 'mm') return `${(m * 1000).toFixed(0)}mm`;
    if (safeUnit === 'ft') return `${(m * 3.28084).toFixed(decimals)}ft`;
    return `${m.toFixed(decimals)}m`;
  };

  return (
    <div className="space-y-2 mt-3" data-testid="surface-image-size-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
          <Maximize2 className="w-3 h-3" /> Image Size on Surface
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 px-2 text-[10px] ${overlayEnabled ? 'text-cyan-300' : 'text-zinc-400'} hover:text-cyan-200`}
          onClick={() => onUpdate({ imageSizeOverlayEnabled: !overlayEnabled })}
          data-testid="surface-image-size-overlay-toggle"
          title="Toggle visual overlay on the surface"
        >
          {overlayEnabled ? 'Overlay: On' : 'Overlay: Off'}
        </Button>
      </div>

      <div className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="text-xs font-medium">Use Surface Image Size</div>
          <div className="text-[10px] text-zinc-500">Recommended</div>
        </div>
        <Switch
          checked={useSurfaceSize}
          onCheckedChange={(v) => onUpdate({ useSurfaceImageSize: v })}
          data-testid="use-surface-image-size-toggle"
        />
      </div>

      {useSurfaceSize && (() => {
        if (!data) {
          return <div className="text-[11px] text-zinc-500 italic">Measuring…</div>;
        }
        if (data.status === 'reverse') {
          return <div className="text-[11px] text-zinc-500 italic">Reverse projector — surface size N/A.</div>;
        }
        if (data.status === 'no-surface') {
          return (
            <div className="text-[11px] text-amber-400 italic" data-testid="surface-status-no-surface">
              No surface in beam — projector aimed at empty space.
            </div>
          );
        }
        const isMixed = data.status === 'mixed';
        const matchPct = Math.round((data.surfaceMatchPct || 0) * 100);
        return (
          <div className="space-y-2" data-testid="surface-image-size-values">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Top edge</div>
                <div className="font-mono text-sm text-cyan-300">{fmt(data.topEdge)}</div>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Bottom edge</div>
                <div className="font-mono text-sm text-cyan-300">{fmt(data.bottomEdge)}</div>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Left edge</div>
                <div className="font-mono text-sm text-cyan-300">{fmt(data.leftEdge)}</div>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Right edge</div>
                <div className="font-mono text-sm text-cyan-300">{fmt(data.rightEdge)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Diagonal (TL→BR)</div>
                <div className="font-mono text-xs text-zinc-300">{fmt(data.diagonal)}</div>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <div className="text-[10px] text-zinc-500">Throw → centre</div>
                <div className="font-mono text-xs text-zinc-300">{fmt(data.throwDistanceCenter)}</div>
              </div>
            </div>
            {isMixed ? (
              <div className="text-[10px] text-amber-400" data-testid="surface-status-mixed">
                ⚠ Mixed surfaces — only {matchPct}% of beam corners hit the primary surface
                {data.primarySurfaceName ? ` (${data.primarySurfaceName})` : ''}.
              </div>
            ) : (
              <div className="text-[10px] text-emerald-400" data-testid="surface-status-ok">
                ✓ Single surface{data.primarySurfaceName ? ` — ${data.primarySurfaceName}` : ''}.
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
});
SurfaceImageSizePanel.displayName = 'SurfaceImageSizePanel';




// Image Width input — local editing state so user can type freely, commits on blur/Enter
const ImageWidthInput = ({ selectedProjector, projectSettings, selectedId, updateProjectorWithMulti }) => {
  const throwDist = selectedProjector.throw_distance || 5;
  const throwR = selectedProjector.throw_ratio || 1.5;
  const currentWidth = throwDist / throwR;
  const displayWidth = metersToUnit(currentWidth, projectSettings.unit);
  const unitCfg = UNITS[projectSettings.unit] || UNITS.m;
  const decimals = unitCfg.decimals ?? 2;

  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(decimals === 0 ? Math.round(displayWidth).toString() : displayWidth.toFixed(decimals));
    }
  }, [displayWidth, isFocused, decimals]);

  const commitValue = (raw) => {
    const newWidthUnit = parseFloat(raw);
    if (isNaN(newWidthUnit) || newWidthUnit <= 0) {
      setLocalValue(decimals === 0 ? Math.round(displayWidth).toString() : displayWidth.toFixed(decimals));
      return;
    }
    const newWidthM = unitToMeters(newWidthUnit, projectSettings.unit);
    const newRatio = throwDist / newWidthM;
    const minRatio = selectedProjector.selected_lens?.throw_ratio_min || selectedProjector.spec?.throw_ratio_min || 0.3;
    const maxRatio = selectedProjector.selected_lens?.throw_ratio_max || selectedProjector.spec?.throw_ratio_max || 10;
    const clampedRatio = Math.min(maxRatio, Math.max(minRatio, newRatio));
    const updates = { throw_ratio: clampedRatio };
    if (selectedProjector.width_locked) {
      updates.locked_width = throwDist / clampedRatio;
    }
    updateProjectorWithMulti(selectedId, updates);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-xs text-zinc-400 flex items-center gap-1.5">
          Image Width ({projectSettings.unit})
          {selectedProjector.width_locked && <Lock className="w-3 h-3 text-amber-400" />}
        </Label>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            step={decimals === 0 ? 1 : Math.pow(10, -decimals)}
            value={isFocused ? localValue : (decimals === 0 ? Math.round(displayWidth) : displayWidth.toFixed(decimals))}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              setLocalValue(decimals === 0 ? Math.round(displayWidth).toString() : displayWidth.toFixed(decimals));
            }}
            onBlur={() => {
              setIsFocused(false);
              commitValue(localValue);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-20 h-6 text-xs font-mono bg-zinc-900 text-right"
            data-testid="image-width-input"
          />
          <button
            onClick={() => {
              const isLocking = !selectedProjector.width_locked;
              const updates = { width_locked: isLocking };
              if (isLocking) {
                updates.locked_width = currentWidth;
              }
              updateProjectorWithMulti(selectedId, updates);
              toast.success(isLocking ? 'Image width locked' : 'Image width unlocked');
            }}
            className={`p-1 rounded transition-colors ${
              selectedProjector.width_locked
                ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
            }`}
            title={selectedProjector.width_locked ? 'Unlock width' : 'Lock width'}
            data-testid="lock-width-btn"
          >
            {selectedProjector.width_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {selectedProjector.width_locked && (
        <div className="text-[10px] text-amber-400/70">
          Width locked — throw ratio adjusts automatically with distance
        </div>
      )}
    </div>
  );
};


// Projector body dimension input - stored in mm internally, displayed in selected unit
const ProjectorDimensionInput = ({ valueMm, onChange, unit, step, label, testId }) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  
  // Convert mm to display unit (mm -> meters -> display unit)
  const valueMeters = (valueMm || 0) / 1000;
  const displayValue = metersToUnit(valueMeters, unit || 'm');
  const decimals = unitConfig?.decimals ?? 2;
  
  // Sync local value when not focused
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(displayValue.toFixed(decimals));
    }
  }, [displayValue, isFocused, decimals]);
  
  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(displayValue.toFixed(decimals));
  };
  
  const handleBlur = () => {
    setIsFocused(false);
    const num = parseFloat(localValue);
    if (!isNaN(num) && num >= 0) {
      // Convert display unit -> meters -> mm
      const mmVal = Math.round(unitToMeters(num, unit || 'm') * 1000);
      onChange(mmVal);
    } else {
      setLocalValue(displayValue.toFixed(decimals));
    }
  };
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };
  
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-zinc-500">{label}</Label>
      <Input
        type="number"
        step={step}
        min="0"
        value={isFocused ? localValue : displayValue.toFixed(decimals)}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs font-mono bg-zinc-900"
        data-testid={testId}
      />
    </div>
  );
};

// Unit-aware Vector3 Input - displays in selected unit, stores in meters
const Vector3Input = ({ label, value, onChange, disabled, step = 0.1, unit = 'm', isPosition = false, decimalsOverride, onSnapToGround }) => {
  const unitConfig = UNITS[unit] || UNITS['m'];
  const decimals = decimalsOverride ?? unitConfig.decimals;
  
  // Convert step to display unit
  const displayStep = isPosition ? step / unitConfig.factor : step;
  
  // Use local state to track the text being typed (in display units)
  const [localValues, setLocalValues] = React.useState({
    x: isPosition ? metersToUnit(value?.x ?? 0, unit) : (value?.x ?? 0),
    y: isPosition ? metersToUnit(value?.y ?? 0, unit) : (value?.y ?? 0),
    z: isPosition ? metersToUnit(value?.z ?? 0, unit) : (value?.z ?? 0)
  });
  const [isEditing, setIsEditing] = React.useState({ x: false, y: false, z: false });

  // Sync with external value when not editing (convert to display units if position)
  React.useEffect(() => {
    if (!isEditing.x && !isEditing.y && !isEditing.z) {
      setLocalValues({
        x: isPosition ? metersToUnit(value?.x ?? 0, unit) : (value?.x ?? 0),
        y: isPosition ? metersToUnit(value?.y ?? 0, unit) : (value?.y ?? 0),
        z: isPosition ? metersToUnit(value?.z ?? 0, unit) : (value?.z ?? 0)
      });
    }
  }, [value, isEditing, unit, isPosition]);

  const handleChange = (axis, val) => {
    setLocalValues(prev => ({
      ...prev,
      [axis]: val
    }));
  };

  const handleFocus = (axis) => {
    setIsEditing(prev => ({ ...prev, [axis]: true }));
    const num = parseFloat(localValues[axis]);
    if (!isNaN(num)) {
      const formatted = decimals === 0 ? Math.round(num).toString() : num.toFixed(decimals);
      setLocalValues(prev => ({ ...prev, [axis]: formatted }));
    }
  };

  const handleBlur = (axis, val) => {
    setIsEditing(prev => ({ ...prev, [axis]: false }));
    
    const num = parseFloat(val);
    if (!isNaN(num)) {
      const storedValue = isPosition ? unitToMeters(num, unit) : num;
      const newValue = { ...value, [axis]: storedValue };
      onChange(newValue);
      // Ground snap: when Y is set to 0 on a position field, trigger snap
      if (isPosition && axis === 'y' && num === 0 && onSnapToGround) {
        onSnapToGround();
      }
    } else {
      onChange({ ...value, [axis]: 0 });
      setLocalValues(prev => ({ ...prev, [axis]: 0 }));
    }
  };

  const handleKeyDown = (e, axis, val) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      // Position: 10mm per arrow key, Rotation/Scale: use step prop (1° for rotation)
      let arrowStep = step;
      if (isPosition) {
        // 10mm = 0.01m, convert to display units
        arrowStep = 0.01 / (UNITS[unit]?.factor || 1);
      }
      const delta = e.key === 'ArrowUp' ? arrowStep : -arrowStep;
      handleIncrement(axis, delta);
    }
  };

  const handleIncrement = (axis, delta) => {
    const currentDisplayVal = parseFloat(localValues[axis]) || 0;
    const newDisplayVal = Math.round((currentDisplayVal + delta) * 100) / 100;
    const storedValue = isPosition ? unitToMeters(newDisplayVal, unit) : newDisplayVal;
    
    onChange({ ...value, [axis]: storedValue });
    setLocalValues(prev => ({ ...prev, [axis]: newDisplayVal }));
  };

  const formatValue = (axis) => {
    if (isEditing[axis]) {
      return localValues[axis];
    }
    const num = parseFloat(localValues[axis]);
    if (isNaN(num)) return '0';
    return decimals === 0 ? Math.round(num).toString() : num.toFixed(decimals);
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-400">{label}</Label>
      <div className="flex gap-1.5">
        {['x', 'y', 'z'].map(axis => (
          <div key={axis} className="flex items-center gap-1 flex-1 min-w-0">
            <span style={{ color: axis === 'x' ? '#ef4444' : axis === 'y' ? '#22c55e' : '#3b82f6', fontWeight: 'bold', fontSize: '11px', flexShrink: 0 }}>
              {axis.toUpperCase()}
            </span>
            <div className="flex flex-col flex-shrink-0">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleIncrement(axis, step); }}
                disabled={disabled}
                className="px-1 text-[10px] leading-3 bg-zinc-700 border border-zinc-600 text-white hover:bg-zinc-600 rounded-t disabled:opacity-50"
              >▲</button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleIncrement(axis, -step); }}
                disabled={disabled}
                className="px-1 text-[10px] leading-3 bg-zinc-700 border border-zinc-600 text-white hover:bg-zinc-600 rounded-b disabled:opacity-50"
              >▼</button>
            </div>
            <Input
              type="text"
              value={formatValue(axis)}
              onChange={(e) => handleChange(axis, e.target.value)}
              onFocus={() => handleFocus(axis)}
              onBlur={(e) => handleBlur(axis, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, axis, e.target.value)}
              disabled={disabled}
              data-testid={`${label.toLowerCase().replace(' ', '-')}-${axis}`}
              className="w-full h-7 text-xs font-mono bg-zinc-900 border-zinc-700"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const PropertiesPanel = () => {
  const { 
    sceneObjects, 
    projectors, 
    selectedObjectId,
    setSelectedObjectId,
    updateSceneObject,
    updateProjector,
    addCustomLens,
    removeSceneObject,
    removeProjector,
    activeRightTab,
    setActiveRightTab,
    projectSettings,
    setProjectSettings,
    worldSettings,
    setWorldSettings,
    overlapAnalysis,
    calculateOverlaps,
    projectorLibrary,
    duplicateSelected,
    duplicateMultiSelected,
    // Multi-select (from global context)
    multiSelectIds,
    setMultiSelectIds,
    // Measurements
    measurements,
    measurementMode,
    setMeasurementMode,
    updateMeasurement,
    removeMeasurement,
    toggleMeasurementVisibility,
    clearAllMeasurements,
    // Edit Mode
    editMode,
    enterEditMode,
    exitEditMode,
    selectedVertices,
    setSelectedVertices,
    clearVertexSelection,
    // Snap-to-ground
    snapToGroundFnRef
  } = useApp();

  // Safe unit configuration - prevents crash when projectSettings.unit is undefined
  const safeUnit = projectSettings?.unit || 'm';
  const safeUnitConfig = UNITS[safeUnit] || UNITS['m'];

  // Center Axis toggle — persisted in localStorage (not project-scoped).
  // Default ON. Updates BabylonScene via 'avst-center-axis-toggle' window event.
  const [showCenterAxis, setShowCenterAxis] = useState(() => {
    try {
      const saved = localStorage.getItem('avst_showCenterAxis');
      return saved === null ? true : saved !== 'false';
    } catch (e) { return true; }
  });

  // Mesh Editor State
  const [meshData, setMeshData] = useState({ positions: [], vertexCount: 0 });
  const [originalMeshData, setOriginalMeshData] = useState(null); // Store original for Cancel
  const [hiddenVertices, setHiddenVertices] = useState(new Set());
  const [viewMode, setViewMode] = useState('wireframe'); // 'wireframe' or 'solid'
  const [undoStack, setUndoStack] = useState([]); // For undo
  const [redoStack, setRedoStack] = useState([]); // For redo
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [lastClickedIndex, setLastClickedIndex] = useState(null); // For shift-click range selection
  const [editModeTab, setEditModeTab] = useState('vertices'); // 'vertices' or 'components'
  const [meshComponents, setMeshComponents] = useState([]); // Component hierarchy
  const [hiddenComponents, setHiddenComponents] = useState(new Set()); // Hidden components
  const [selectedComponents, setSelectedComponents] = useState([]); // Selected components
  const [lockProportions, setLockProportions] = useState(false); // Lock dimension proportions
  const [editDeckLevel, setEditDeckLevel] = useState(null); // Selected deck level for editing
  const [editDeckIndex, setEditDeckIndex] = useState(0); // Selected deck index within the level
  const [editStairLevel, setEditStairLevel] = useState(null); // Selected stair level for editing
  const [activeScaffoldTab, setActiveScaffoldTab] = useState('primary'); // 'primary' or support index (0,1,2...)
  
  // Get mesh data when entering edit mode - store original for cancel
  useEffect(() => {
    if (editMode && window.getEditModeVertexData) {
      const timeoutId = setTimeout(() => {
        const data = window.getEditModeVertexData();
        if (data) {
          const newData = {
            positions: [...data.positions],
            vertexCount: data.vertexCount
          };
          setMeshData(newData);
          // Store original for cancel - deep copy
          setOriginalMeshData({
            positions: [...data.positions],
            vertexCount: data.vertexCount
          });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [editMode]);
  
  // Get mesh components when entering edit mode
  useEffect(() => {
    if (editMode && window.getMeshComponents) {
      const timeoutId = setTimeout(() => {
        const components = window.getMeshComponents();
        if (components) {
          setMeshComponents(components);
          // Initialize hiddenComponents based on actual mesh visibility state
          const hiddenIds = new Set();
          components.forEach(c => {
            if (c.isVisible === false) {
              hiddenIds.add(c.id);
            }
          });
          if (hiddenIds.size > 0) {
            logger.log('Initializing hidden components from mesh state:', hiddenIds.size);
            setHiddenComponents(hiddenIds);
          }
        }
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [editMode]);

  // Update 3D view mode when toggle changes
  useEffect(() => {
    if (editMode && window.setEditModeViewMode) {
      window.setEditModeViewMode(viewMode);
    }
  }, [editMode, viewMode]);

  // Update 3D hidden vertices when hiddenVertices changes
  useEffect(() => {
    if (editMode && window.updateHiddenVertices) {
      window.updateHiddenVertices(Array.from(hiddenVertices));
    }
  }, [editMode, hiddenVertices]);

  // Clear mesh data when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      const timeoutId = setTimeout(() => {
        setMeshData({ positions: [], vertexCount: 0 });
        setOriginalMeshData(null);
        setHiddenVertices(new Set());
        setUndoStack([]);
        setRedoStack([]);
        setLastClickedIndex(null);
        setEditModeTab('vertices');
        setMeshComponents([]);
        setHiddenComponents(new Set());
        setSelectedComponents([]);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [editMode]);

  // Listen for component clicks from 3D scene - auto-select in UI
  useEffect(() => {
    if (!editMode) return;
    
    // Set up callback for when a component is clicked in 3D view
    window.onComponentClicked = (meshId, meshName) => {
      logger.log('Component clicked callback:', meshId, meshName);
      
      // Switch to components tab
      setEditModeTab('components');
      
      // Find the matching component in our list
      const matchingComponent = meshComponents.find(c => 
        c.id === meshId || c.name === meshName
      );
      
      if (matchingComponent) {
        // Replace selection with clicked component (not toggle)
        setSelectedComponents([matchingComponent.id]);
        
        // Highlight in 3D view
        if (window.highlightComponents) {
          window.highlightComponents([matchingComponent.id]);
        }
        
        // Scroll to the component in the list after a brief delay
        setTimeout(() => {
          const componentIndex = meshComponents.findIndex(c => c.id === matchingComponent.id);
          const element = document.querySelector(`[data-testid="component-${componentIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      } else {
        // Try fuzzy matching by checking if name contains the meshId or meshName
        const fuzzyMatch = meshComponents.find(c => 
          c.id?.includes(meshId) || meshId?.includes(c.id) ||
          c.name?.includes(meshName) || meshName?.includes(c.name)
        );
        
        if (fuzzyMatch) {
          setSelectedComponents([fuzzyMatch.id]);
          if (window.highlightComponents) {
            window.highlightComponents([fuzzyMatch.id]);
          }
          
          setTimeout(() => {
            const componentIndex = meshComponents.findIndex(c => c.id === fuzzyMatch.id);
            const element = document.querySelector(`[data-testid="component-${componentIndex}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 50);
        }
      }
    };
    
    return () => {
      window.onComponentClicked = null;
    };
  }, [editMode, meshComponents]);

  // Process vertices into list with names - filter out hidden ones for display
  const vertexList = useMemo(() => {
    const positions = meshData?.positions;
    if (!positions || positions.length === 0) return [];
    
    const vertices = [];
    const seen = new Map();
    
    for (let i = 0; i < positions.length / 3; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      const key = `${x.toFixed(3)}_${y.toFixed(3)}_${z.toFixed(3)}`;
      
      if (!seen.has(key)) {
        const vertexNum = vertices.length + 1;
        seen.set(key, vertexNum);
        vertices.push({
          index: i,
          name: `Vertex ${vertexNum}`,
          x: x.toFixed(2),
          y: y.toFixed(2),
          z: z.toFixed(2),
          key,
          hidden: hiddenVertices.has(i)
        });
      }
    }
    return vertices;
  }, [meshData, hiddenVertices]);

  // Get visible vertices (not hidden)
  const visibleVertexList = useMemo(() => {
    return vertexList.filter(v => !v.hidden);
  }, [vertexList]);

  // Vertex selection handlers with proper Ctrl+Shift support
  const handleVertexClick = useCallback((index, e) => {
    e.stopPropagation();
    
    if (e.ctrlKey || e.metaKey) {
      // Ctrl+Click: Toggle individual vertex
      setSelectedVertices(prev => {
        if (prev.includes(index)) {
          return prev.filter(i => i !== index);
        }
        return [...prev, index];
      });
      setLastClickedIndex(index);
    } else if (e.shiftKey && lastClickedIndex !== null) {
      // Shift+Click: Select range from last clicked to current
      const visibleIndices = visibleVertexList.map(v => v.index);
      const lastIdx = visibleIndices.indexOf(lastClickedIndex);
      const currentIdx = visibleIndices.indexOf(index);
      
      if (lastIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(lastIdx, currentIdx);
        const end = Math.max(lastIdx, currentIdx);
        const rangeIndices = visibleIndices.slice(start, end + 1);
        
        setSelectedVertices(prev => {
          const newSet = new Set([...prev, ...rangeIndices]);
          return Array.from(newSet);
        });
      }
    } else {
      // Normal click: Select only this vertex
      setSelectedVertices([index]);
      setLastClickedIndex(index);
    }
  }, [setSelectedVertices, lastClickedIndex, visibleVertexList]);

  const handleSelectAllVertices = useCallback(() => {
    setSelectedVertices(visibleVertexList.map(v => v.index));
  }, [visibleVertexList, setSelectedVertices]);

  // Select vertices by section (region)
  const handleSelectSection = useCallback((section) => {
    if (!meshData?.positions || meshData.positions.length === 0) return;
    
    const positions = meshData.positions;
    const vertexCount = meshData.vertexCount;
    
    // Calculate bounding box
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    for (let i = 0; i < vertexCount; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
    }
    
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    const midZ = (minZ + maxZ) / 2;
    
    const selectedIndices = [];
    
    // Filter vertices based on section
    for (let i = 0; i < vertexCount; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      
      let include = false;
      switch (section) {
        case 'top':
          include = y >= midY;
          break;
        case 'bottom':
          include = y < midY;
          break;
        case 'front':
          include = z >= midZ;
          break;
        case 'back':
          include = z < midZ;
          break;
        case 'left':
          include = x < midX;
          break;
        case 'right':
          include = x >= midX;
          break;
        case 'topQuarter':
          include = y >= minY + (maxY - minY) * 0.75;
          break;
        case 'bottomQuarter':
          include = y <= minY + (maxY - minY) * 0.25;
          break;
        case 'center':
          include = x >= minX + (maxX - minX) * 0.25 && x <= minX + (maxX - minX) * 0.75 &&
                   y >= minY + (maxY - minY) * 0.25 && y <= minY + (maxY - minY) * 0.75 &&
                   z >= minZ + (maxZ - minZ) * 0.25 && z <= minZ + (maxZ - minZ) * 0.75;
          break;
        default:
          break;
      }
      
      if (include && !hiddenVertices.has(i)) {
        selectedIndices.push(i);
      }
    }
    
    if (selectedIndices.length > 0) {
      setSelectedVertices(selectedIndices);
      toast.success(`Selected ${selectedIndices.length} vertices (${section})`);
    } else {
      toast.info('No vertices found in this section');
    }
  }, [meshData, hiddenVertices, setSelectedVertices]);

  // Hide vertex (soft delete - can undo)
  const handleHideVertex = useCallback((index) => {
    // Save state for undo
    setUndoStack(prev => [...prev, { type: 'hide', indices: [index], hiddenBefore: new Set(hiddenVertices) }]);
    setRedoStack([]);
    
    setHiddenVertices(prev => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
    toast.success('Vertex hidden (can undo)');
  }, [hiddenVertices]);

  // Unhide vertex
  const handleUnhideVertex = useCallback((index) => {
    setUndoStack(prev => [...prev, { type: 'unhide', indices: [index], hiddenBefore: new Set(hiddenVertices) }]);
    setRedoStack([]);
    
    setHiddenVertices(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    toast.success('Vertex restored');
  }, [hiddenVertices]);

  // Component handlers
  const handleToggleComponent = useCallback((componentId) => {
    setSelectedComponents(prev => {
      const newSelection = prev.includes(componentId) 
        ? prev.filter(c => c !== componentId)
        : [...prev, componentId];
      
      // Highlight selected components in 3D view
      if (window.highlightComponents) {
        window.highlightComponents(newSelection);
      }
      
      return newSelection;
    });
  }, []);

  const handleHideComponent = useCallback((componentId) => {
    // Find component info for undo
    const component = meshComponents.find(c => c.id === componentId);
    
    // Add to undo stack
    setUndoStack(prev => [...prev, { 
      type: 'hideComponent', 
      componentId, 
      componentName: component?.name,
      hiddenBefore: new Set(hiddenComponents) 
    }]);
    setRedoStack([]);
    
    setHiddenComponents(prev => {
      const newSet = new Set(prev);
      newSet.add(componentId);
      return newSet;
    });
    // Also hide in 3D view
    if (window.hideComponent) {
      window.hideComponent(componentId);
    }
    toast.success('Component hidden');
  }, [meshComponents, hiddenComponents]);

  const handleShowComponent = useCallback((componentId) => {
    // Add to undo stack
    setUndoStack(prev => [...prev, { 
      type: 'showComponent', 
      componentId, 
      hiddenBefore: new Set(hiddenComponents) 
    }]);
    setRedoStack([]);
    
    setHiddenComponents(prev => {
      const newSet = new Set(prev);
      newSet.delete(componentId);
      return newSet;
    });
    // Also show in 3D view
    if (window.showComponent) {
      window.showComponent(componentId);
    }
  }, [hiddenComponents]);

  // Select all components
  const handleSelectAllComponents = useCallback(() => {
    const allIds = meshComponents.filter(c => !hiddenComponents.has(c.id)).map(c => c.id);
    setSelectedComponents(allIds);
    if (window.highlightComponents) {
      window.highlightComponents(allIds);
    }
  }, [meshComponents, hiddenComponents]);

  // Clear component selection
  const handleClearComponentSelection = useCallback(() => {
    setSelectedComponents([]);
    if (window.clearComponentHighlights) {
      window.clearComponentHighlights();
    }
  }, []);

  const handleDeleteComponent = useCallback((componentId) => {
    // Find component info for undo
    const component = meshComponents.find(c => c.id === componentId);
    
    // Add to undo stack (store component info for potential restore)
    setUndoStack(prev => [...prev, { 
      type: 'deleteComponent', 
      componentId,
      componentInfo: component,
      meshComponentsBefore: [...meshComponents]
    }]);
    setRedoStack([]);
    
    if (window.deleteComponent) {
      window.deleteComponent(componentId);
      // Remove from list
      setMeshComponents(prev => prev.filter(c => c.id !== componentId));
      setSelectedComponents(prev => prev.filter(c => c !== componentId));
      toast.success('Component deleted (Ctrl+Z to undo)');
    }
  }, [meshComponents]);

  const handleDeleteSelectedComponents = useCallback(() => {
    if (selectedComponents.length === 0) {
      toast.error('Select components first');
      return;
    }
    
    // Store deleted components for undo
    const deletedComponents = meshComponents.filter(c => selectedComponents.includes(c.id));
    
    // Add to undo stack
    setUndoStack(prev => [...prev, { 
      type: 'deleteComponents', 
      componentIds: [...selectedComponents],
      deletedComponents,
      meshComponentsBefore: [...meshComponents]
    }]);
    setRedoStack([]);
    
    selectedComponents.forEach(componentId => {
      if (window.deleteComponent) {
        window.deleteComponent(componentId);
      }
    });
    setMeshComponents(prev => prev.filter(c => !selectedComponents.includes(c.id)));
    setSelectedComponents([]);
    toast.success(`Deleted ${selectedComponents.length} components (Ctrl+Z to undo)`);
  }, [selectedComponents, meshComponents]);

  // Hide selected components (bulk hide)
  const handleHideSelectedComponents = useCallback(() => {
    if (selectedComponents.length === 0) {
      toast.error('Select components first');
      return;
    }
    
    // Add to undo stack
    setUndoStack(prev => [...prev, { 
      type: 'hideComponents', 
      componentIds: [...selectedComponents],
      hiddenBefore: new Set(hiddenComponents)
    }]);
    setRedoStack([]);
    
    selectedComponents.forEach(componentId => {
      if (window.hideComponent) {
        window.hideComponent(componentId);
      }
    });
    setHiddenComponents(prev => {
      const newSet = new Set(prev);
      selectedComponents.forEach(id => newSet.add(id));
      return newSet;
    });
    setSelectedComponents([]);
    toast.success(`Hidden ${selectedComponents.length} components (Ctrl+Z to undo)`);
  }, [selectedComponents, hiddenComponents]);

  // Delete vertex (permanent - but can be undone)
  const handleDeleteVertex = useCallback((index) => {
    if (window.deleteVerticesFromMesh) {
      // Delete returns the before state for undo
      const beforeState = window.deleteVerticesFromMesh([index]);
      if (beforeState) {
        setUndoStack(prev => [...prev, { type: 'delete', indices: [index], beforeState }]);
        setRedoStack([]);
      }
      
      setTimeout(() => {
        if (window.getEditModeVertexData) {
          const data = window.getEditModeVertexData();
          if (data) {
            setMeshData({
              positions: data.positions,
              vertexCount: data.vertexCount
            });
          }
        }
      }, 100);
      toast.success('Vertex deleted (can undo)');
    }
  }, []);

  // Delete selected vertices
  const handleDeleteSelectedVertices = useCallback(() => {
    if (selectedVertices.length === 0) {
      toast.error('Select vertices first');
      return;
    }
    if (window.deleteVerticesFromMesh) {
      // Delete returns the before state for undo
      const beforeState = window.deleteVerticesFromMesh(selectedVertices);
      if (beforeState) {
        setUndoStack(prev => [...prev, { type: 'delete', indices: [...selectedVertices], beforeState }]);
        setRedoStack([]);
      }
      
      clearVertexSelection();
      setTimeout(() => {
        if (window.getEditModeVertexData) {
          const data = window.getEditModeVertexData();
          if (data) {
            setMeshData({
              positions: data.positions,
              vertexCount: data.vertexCount
            });
          }
        }
      }, 100);
      toast.success(`Deleted ${selectedVertices.length} vertices`);
    } else {
      toast.error('Delete not available');
    }
  }, [selectedVertices, clearVertexSelection, meshData]);

  // Hide selected vertices (bulk hide)
  const handleHideSelectedVertices = useCallback(() => {
    if (selectedVertices.length === 0) {
      toast.error('Select vertices first');
      return;
    }
    // Save state for undo
    setUndoStack(prev => [...prev, { type: 'hide', indices: [...selectedVertices], hiddenBefore: new Set(hiddenVertices) }]);
    setRedoStack([]);
    
    setHiddenVertices(prev => {
      const newSet = new Set(prev);
      selectedVertices.forEach(idx => newSet.add(idx));
      return newSet;
    });
    clearVertexSelection();
    toast.success(`Hidden ${selectedVertices.length} vertices`);
  }, [selectedVertices, hiddenVertices, clearVertexSelection]);

  // Unhide all vertices
  const handleUnhideAllVertices = useCallback(() => {
    if (hiddenVertices.size === 0) {
      toast.info('No hidden vertices');
      return;
    }
    setUndoStack(prev => [...prev, { type: 'unhideAll', hiddenBefore: new Set(hiddenVertices) }]);
    setRedoStack([]);
    setHiddenVertices(new Set());
    toast.success('All vertices visible');
  }, [hiddenVertices]);

  // UNDO handler
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) {
      toast.info('Nothing to undo');
      return;
    }
    
    const lastAction = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    // VERTEX operations
    if (lastAction.type === 'hide') {
      // Restore hidden state to before hide
      setRedoStack(prev => [...prev, { type: 'hide', indices: lastAction.indices, hiddenBefore: new Set(hiddenVertices) }]);
      setHiddenVertices(lastAction.hiddenBefore);
      toast.success('Undo: vertices restored');
    } else if (lastAction.type === 'unhide' || lastAction.type === 'unhideAll') {
      // Restore hidden state
      setRedoStack(prev => [...prev, { type: lastAction.type, hiddenBefore: new Set(hiddenVertices) }]);
      setHiddenVertices(lastAction.hiddenBefore);
      toast.success('Undo: hide action restored');
    } else if (lastAction.type === 'delete') {
      // Restore mesh to state before deletion
      if (lastAction.beforeState && window.restoreMeshToState) {
        // Save current state for redo
        const currentPositions = window._editModeMesh?.getVerticesData?.(window.BABYLON?.VertexBuffer?.PositionKind);
        const currentIndices = window._editModeMesh?.getIndices?.();
        
        setRedoStack(prev => [...prev, { 
          type: 'delete', 
          indices: lastAction.indices,
          afterState: {
            positions: currentPositions ? [...currentPositions] : [],
            indices: currentIndices ? [...currentIndices] : []
          },
          beforeState: lastAction.beforeState 
        }]);
        
        window.restoreMeshToState(lastAction.beforeState);
        
        // Refresh mesh data
        setTimeout(() => {
          if (window.getEditModeVertexData) {
            const data = window.getEditModeVertexData();
            if (data) {
              setMeshData({
                positions: data.positions,
                vertexCount: data.vertexCount
              });
            }
          }
        }, 100);
        toast.success('Undo: vertices restored');
      } else {
        toast.error('Cannot undo delete - state not saved');
      }
    }
    // COMPONENT operations
    else if (lastAction.type === 'hideComponent' || lastAction.type === 'hideComponents') {
      // Restore hidden components state
      setRedoStack(prev => [...prev, { ...lastAction, hiddenAfter: new Set(hiddenComponents) }]);
      setHiddenComponents(lastAction.hiddenBefore);
      // Show components in 3D view
      if (lastAction.componentId && window.showComponent) {
        window.showComponent(lastAction.componentId);
      } else if (lastAction.componentIds) {
        lastAction.componentIds.forEach(id => {
          if (window.showComponent) window.showComponent(id);
        });
      }
      toast.success('Undo: component(s) restored');
    } else if (lastAction.type === 'showComponent') {
      // Re-hide the component
      setRedoStack(prev => [...prev, { ...lastAction, hiddenAfter: new Set(hiddenComponents) }]);
      setHiddenComponents(lastAction.hiddenBefore);
      if (window.hideComponent) {
        window.hideComponent(lastAction.componentId);
      }
      toast.success('Undo: component hidden again');
    } else if (lastAction.type === 'deleteComponent' || lastAction.type === 'deleteComponents') {
      // Restore deleted components - we need to reload the model
      // For now, restore the list and show a message
      setRedoStack(prev => [...prev, { ...lastAction, meshComponentsAfter: [...meshComponents] }]);
      setMeshComponents(lastAction.meshComponentsBefore);
      // Restore in 3D view if possible
      if (window.restoreDeletedComponents) {
        const idsToRestore = lastAction.componentId ? [lastAction.componentId] : lastAction.componentIds;
        window.restoreDeletedComponents(idsToRestore);
      }
      toast.success('Undo: component(s) restored (click Done and re-enter edit mode to see in 3D)');
    }
  }, [undoStack, hiddenVertices, hiddenComponents, meshComponents]);

  // REDO handler
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) {
      toast.info('Nothing to redo');
      return;
    }
    
    const lastAction = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    // VERTEX operations
    if (lastAction.type === 'hide') {
      // Re-hide vertices
      setUndoStack(prev => [...prev, { type: 'hide', indices: lastAction.indices, hiddenBefore: new Set(hiddenVertices) }]);
      setHiddenVertices(lastAction.hiddenBefore);
      toast.success('Redo: vertices hidden');
    } else if (lastAction.type === 'unhide' || lastAction.type === 'unhideAll') {
      setUndoStack(prev => [...prev, { type: lastAction.type, hiddenBefore: new Set(hiddenVertices) }]);
      setHiddenVertices(lastAction.hiddenBefore);
      toast.success('Redo: vertices shown');
    } else if (lastAction.type === 'delete') {
      // Re-delete vertices
      if (lastAction.beforeState && window.deleteVerticesFromMesh) {
        // Save current state for undo again
        setUndoStack(prev => [...prev, { type: 'delete', indices: lastAction.indices, beforeState: lastAction.beforeState }]);
        
        // Apply deletion
        window.deleteVerticesFromMesh(lastAction.indices);
        
        // Refresh mesh data
        setTimeout(() => {
          if (window.getEditModeVertexData) {
            const data = window.getEditModeVertexData();
            if (data) {
              setMeshData({
                positions: data.positions,
                vertexCount: data.vertexCount
              });
            }
          }
        }, 100);
        toast.success('Redo: vertices deleted');
      }
    }
    // COMPONENT operations
    else if (lastAction.type === 'hideComponent' || lastAction.type === 'hideComponents') {
      // Re-hide components
      setUndoStack(prev => [...prev, { ...lastAction, hiddenBefore: new Set(hiddenComponents) }]);
      setHiddenComponents(lastAction.hiddenAfter);
      // Hide in 3D view
      if (lastAction.componentId && window.hideComponent) {
        window.hideComponent(lastAction.componentId);
      } else if (lastAction.componentIds) {
        lastAction.componentIds.forEach(id => {
          if (window.hideComponent) window.hideComponent(id);
        });
      }
      toast.success('Redo: component(s) hidden');
    } else if (lastAction.type === 'showComponent') {
      // Re-show the component
      setUndoStack(prev => [...prev, { ...lastAction, hiddenBefore: new Set(hiddenComponents) }]);
      setHiddenComponents(lastAction.hiddenAfter);
      if (window.showComponent) {
        window.showComponent(lastAction.componentId);
      }
      toast.success('Redo: component shown');
    } else if (lastAction.type === 'deleteComponent' || lastAction.type === 'deleteComponents') {
      // Re-delete components
      setUndoStack(prev => [...prev, { ...lastAction, meshComponentsBefore: [...meshComponents] }]);
      setMeshComponents(lastAction.meshComponentsAfter);
      // Delete in 3D view
      const idsToDelete = lastAction.componentId ? [lastAction.componentId] : lastAction.componentIds;
      idsToDelete.forEach(id => {
        if (window.deleteComponent) window.deleteComponent(id);
      });
      toast.success('Redo: component(s) deleted');
    }
  }, [redoStack, hiddenVertices, hiddenComponents, meshComponents]);

  // Keyboard shortcuts for Undo/Redo in edit mode
  useEffect(() => {
    if (!editMode) return;
    
    const handleKeyDown = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      
      // Undo: Ctrl+Z (without Shift)
      if (isMeta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Shift+Z or Ctrl+Y
      else if ((isMeta && e.key === 'z' && e.shiftKey) || (isMeta && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, handleUndo, handleRedo]);

  // Handle Cancel - restore original mesh data
  const handleCancelEditMode = useCallback(() => {
    if (originalMeshData && window.restoreOriginalMesh) {
      window.restoreOriginalMesh(originalMeshData);
    }
    exitEditMode(false);
  }, [originalMeshData, exitEditMode]);

  // Handle Save As to Library
  const handleSaveAsToLibrary = useCallback(async () => {
    if (!saveAsName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      // Get current mesh data
      if (window.exportEditedMesh) {
        const meshExportData = window.exportEditedMesh();
        if (meshExportData) {
          // Save to library via API
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/library/save-mesh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {}) },
            body: JSON.stringify({
              name: saveAsName.trim(),
              meshData: meshExportData,
              hiddenVertices: Array.from(hiddenVertices)
            })
          });
          
          if (response.ok) {
            toast.success(`Saved "${saveAsName}" to library`);
            setSaveAsDialogOpen(false);
            setSaveAsName('');
          } else {
            toast.error('Failed to save to library');
          }
        }
      } else {
        toast.error('Export not available');
      }
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save');
    }
  }, [saveAsName, hiddenVertices]);

  const uvFileInputRef = useRef(null);
  const projectorImageInputRef = useRef(null);
  const textureInputRef = useRef(null);
  const mtlFileInputRef = useRef(null);
  const [addLensOpen, setAddLensOpen] = useState(false);
  const [newLens, setNewLens] = useState({ name: '', throw_ratio_min: 1.0, throw_ratio_max: 2.0, lens_shift_h: 30, lens_shift_v: 60, weight: 0 });
  
  // Content Browser state
  const [contentBrowserOpen, setContentBrowserOpen] = useState(false);
  const [contentBrowserMode, setContentBrowserMode] = useState(null); // 'texture' or 'projector_image'

  const selectedObject = sceneObjects.find(obj => obj.id === selectedObjectId);
  const selectedProjector = projectors.find(proj => proj.id === selectedObjectId);
  const selected = selectedObject || selectedProjector;
  const isProjector = !!selectedProjector;

  // Handler for proportional dimension changes
  // For imported models (type='model'), changing dimensions changes scale instead of recreating
  const handleDimensionChange = useCallback((dimension, newValue) => {
    if (!selectedObject) return;

    const currentDims = selectedObject.dimensions || { width: 1, height: 1, depth: 1 };
    const currentScale = selectedObject.scale || { x: 1, y: 1, z: 1 };
    // Imported 3D models are loaded with their dimensions baked into the
    // mesh vertices and `scale` acts as a multiplier on top. The Width/
    // Height/Depth input therefore shows the EFFECTIVE size = dim × scale,
    // not the raw `dimensions` value. Compute that effective size as
    // `oldValue` so a `newValue / oldValue` ratio gives a correct delta.
    const isImportedModel = selectedObject.type === 'mesh' || selectedObject.type === 'model' || selectedObject.fileId || selectedObject.file_id;
    const scaleKeyForDim = dimension === 'width' ? 'x' : dimension === 'height' ? 'y' : 'z';
    const oldValue = isImportedModel
      ? (currentDims[dimension] || 1) * (currentScale[scaleKeyForDim] || 1)
      : (currentDims[dimension] || 1);

    if (isImportedModel) {
      // For imported 3D models the bake-on-reload flow in BabylonScene treats
      // `dimensions` as the BAKED bounding box (set ONCE at first import) and
      // `scale` as the user's gizmo multiplier on top. Visible size is
      // `dimensions × scale`. So when the user types a new size into the
      // Width/Height/Depth input we must update ONLY scale, not dimensions —
      // updating both would cause the scale factor to be applied twice on the
      // next reload (once via vertex re-bake to match dimensions, once via
      // meshParent.scaling = savedScale), producing the classic "object keeps
      // shrinking on every refresh" bug.

      if (lockProportions && oldValue !== 0 && newValue !== oldValue) {
        // Uniform scaling for proportional lock
        const scaleFactor = newValue / oldValue;
        const newScale = {
          x: currentScale.x * scaleFactor,
          y: currentScale.y * scaleFactor,
          z: currentScale.z * scaleFactor
        };
        updateSceneObjectWithMulti(selectedObject.id, { scale: newScale });
      } else {
        // Non-uniform scaling - adjust scale for the specific dimension
        const scaleFactorForDim = oldValue !== 0 ? newValue / oldValue : 1;
        const scaleKey = dimension === 'width' ? 'x' : dimension === 'height' ? 'y' : 'z';
        const newScale = {
          ...currentScale,
          [scaleKey]: currentScale[scaleKey] * scaleFactorForDim
        };
        updateSceneObjectWithMulti(selectedObject.id, { scale: newScale });
      }
    } else {
      // For primitives, recreate them (existing behavior)
      // Special handling for cylinder/pipe: depth is not part of the geometry
      // so we need to use scale.z to represent it
      const isCylinderType = selectedObject.type === 'cylinder' || selectedObject.type === 'pipe';
      
      if (lockProportions && oldValue !== 0 && newValue !== oldValue) {
        // Calculate scale factor
        const scaleFactor = newValue / oldValue;
        
        // Scale all dimensions proportionally
        const newDims = {
          width: currentDims.width * scaleFactor,
          height: currentDims.height * scaleFactor,
          depth: currentDims.depth * scaleFactor,
        };
        
        if (isCylinderType) {
          // For cylinder/pipe, update scale.z to reflect depth/width ratio
          const newScaleZ = (newDims.depth || 1) / (newDims.width || 1);
          updateSceneObjectWithMulti(selectedObject.id, { 
            dimensions: newDims,
            scale: { ...(selectedObject.scale || { x: 1, y: 1, z: 1 }), z: newScaleZ }
          });
        } else {
          updateSceneObjectWithMulti(selectedObject.id, { dimensions: newDims });
        }
      } else {
        const newDims = { ...currentDims, [dimension]: newValue };
        
        if (isCylinderType) {
          // For cylinder/pipe, always sync scale.z with depth/width ratio
          const newScaleZ = (newDims.depth || 1) / (newDims.width || 1);
          updateSceneObjectWithMulti(selectedObject.id, { 
            dimensions: newDims,
            scale: { ...(selectedObject.scale || { x: 1, y: 1, z: 1 }), z: newScaleZ }
          });
        } else {
          // Just update the single dimension
          updateSceneObjectWithMulti(selectedObject.id, { 
            dimensions: newDims
          });
        }
      }
    }
  }, [selectedObject, lockProportions, updateSceneObject]);

  const handleDelete = () => {
    // If multi-select is active, delete all selected items
    if (multiSelectIds.length > 0) {
      handleMultiDelete();
      return;
    }
    if (isProjector) {
      removeProjector(selectedObjectId);
    } else {
      removeSceneObject(selectedObjectId);
    }
  };

  const handleUVMapUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObject) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      updateSceneObjectWithMulti(selectedObject.id, { 
        uvMapUrl: event.target.result,
        uvMap: file.name
      });
      toast.success('UV map applied');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Handle texture upload for 3D model
  const handleTextureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObject) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      // Store texture URL - actual texture loading handled by BabylonScene
      updateSceneObjectWithMulti(selectedObject.id, { 
        textureUrl: event.target.result,
        textureName: file.name
      });
      toast.success('Texture applied to model');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Handle MTL file upload for object material look & feel
  const handleMTLUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObject) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      updateSceneObjectWithMulti(selectedObject.id, {
        mtlData: event.target.result,
        mtlName: file.name
      });
      toast.success(`Material file "${file.name}" applied`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleRemoveMTL = () => {
    if (!selectedObject) return;
    updateSceneObjectWithMulti(selectedObject.id, {
      mtlData: null,
      mtlName: null
    });
    toast.success('Material file removed');
  };

  // Auto-create UV unwrap and export
  const handleAutoUVCreate = () => {
    if (!selectedObject) return;
    
    // Create a simple UV unwrap image (box projection style)
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    
    // Draw UV layout based on object type
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw grid lines for UV reference
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 8; i++) {
      ctx.beginPath();
      ctx.moveTo(i * canvas.width / 8, 0);
      ctx.lineTo(i * canvas.width / 8, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * canvas.height / 8);
      ctx.lineTo(canvas.width, i * canvas.height / 8);
      ctx.stroke();
    }
    
    // Draw object name and UV info
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`UV Map: ${selectedObject.name}`, canvas.width / 2, 40);
    
    // Draw UV shells based on object type
    ctx.strokeStyle = '#d946ef';
    ctx.lineWidth = 2;
    
    const type = selectedObject.type === 'primitive' ? selectedObject.primitive_type : selectedObject.type;
    
    if (type === 'cube' || type === 'box') {
      // Draw cube unwrap (cross pattern)
      const size = 150;
      const startX = canvas.width / 2 - size * 1.5;
      const startY = canvas.height / 2 - size;
      
      // Top
      ctx.strokeRect(startX + size, startY - size, size, size);
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(startX + size + 2, startY - size + 2, size - 4, size - 4);
      ctx.fillStyle = '#fff';
      ctx.font = '16px Arial';
      ctx.fillText('TOP', startX + size * 1.5, startY - size / 2);
      
      // Front, Left, Back, Right
      const labels = ['LEFT', 'FRONT', 'RIGHT', 'BACK'];
      const colors = ['#ef4444', '#22c55e', '#f59e0b', '#8b5cf6'];
      for (let i = 0; i < 4; i++) {
        ctx.strokeStyle = '#d946ef';
        ctx.strokeRect(startX + i * size, startY, size, size);
        ctx.fillStyle = colors[i];
        ctx.fillRect(startX + i * size + 2, startY + 2, size - 4, size - 4);
        ctx.fillStyle = '#fff';
        ctx.fillText(labels[i], startX + i * size + size / 2, startY + size / 2);
      }
      
      // Bottom
      ctx.strokeStyle = '#d946ef';
      ctx.strokeRect(startX + size, startY + size, size, size);
      ctx.fillStyle = '#06b6d4';
      ctx.fillRect(startX + size + 2, startY + size + 2, size - 4, size - 4);
      ctx.fillStyle = '#fff';
      ctx.fillText('BOTTOM', startX + size * 1.5, startY + size * 1.5);
    } else if (type === 'plane') {
      // Single rectangle for plane
      const size = 400;
      const startX = (canvas.width - size) / 2;
      const startY = (canvas.height - size) / 2;
      ctx.strokeRect(startX, startY, size, size);
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(startX + 2, startY + 2, size - 4, size - 4);
      ctx.fillStyle = '#fff';
      ctx.font = '24px Arial';
      ctx.fillText('PLANE', canvas.width / 2, canvas.height / 2);
    } else if (type === 'sphere') {
      // Spherical UV (rectangular with distortion lines)
      const width = 600;
      const height = 300;
      const startX = (canvas.width - width) / 2;
      const startY = (canvas.height - height) / 2;
      ctx.strokeRect(startX, startY, width, height);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(startX + 2, startY + 2, width - 4, height - 4);
      // Draw latitude/longitude lines
      ctx.strokeStyle = '#ffffff33';
      for (let i = 1; i < 6; i++) {
        ctx.beginPath();
        ctx.moveTo(startX + i * width / 6, startY);
        ctx.lineTo(startX + i * width / 6, startY + height);
        ctx.stroke();
      }
      for (let i = 1; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(startX, startY + i * height / 3);
        ctx.lineTo(startX + width, startY + i * height / 3);
        ctx.stroke();
      }
      ctx.fillStyle = '#fff';
      ctx.font = '24px Arial';
      ctx.fillText('SPHERE (Equirectangular)', canvas.width / 2, canvas.height / 2);
    } else if (type === 'cylinder') {
      // Cylinder UV (rectangle + 2 circles)
      const bodyWidth = 500;
      const bodyHeight = 200;
      const capSize = 100;
      const startX = (canvas.width - bodyWidth) / 2;
      const startY = 300;
      
      // Body
      ctx.strokeRect(startX, startY, bodyWidth, bodyHeight);
      ctx.fillStyle = '#8b5cf6';
      ctx.fillRect(startX + 2, startY + 2, bodyWidth - 4, bodyHeight - 4);
      ctx.fillStyle = '#fff';
      ctx.fillText('BODY', canvas.width / 2, startY + bodyHeight / 2);
      
      // Top cap
      ctx.beginPath();
      ctx.arc(canvas.width / 2 - capSize * 1.5, startY - capSize - 20, capSize, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#3b82f6';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText('TOP', canvas.width / 2 - capSize * 1.5, startY - capSize - 20);
      
      // Bottom cap
      ctx.beginPath();
      ctx.arc(canvas.width / 2 + capSize * 1.5, startY - capSize - 20, capSize, 0, Math.PI * 2);
      ctx.strokeStyle = '#d946ef';
      ctx.stroke();
      ctx.fillStyle = '#06b6d4';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText('BOTTOM', canvas.width / 2 + capSize * 1.5, startY - capSize - 20);
    }
    
    // Add instructions
    ctx.fillStyle = '#666';
    ctx.font = '14px Arial';
    ctx.fillText('Export this image and use it as a template for creating projection content', canvas.width / 2, canvas.height - 40);
    ctx.fillText('Paint your content on each face, then re-import as UV Map', canvas.width / 2, canvas.height - 20);
    
    // Convert to data URL and save
    const dataUrl = canvas.toDataURL('image/png');
    
    updateSceneObjectWithMulti(selectedObject.id, {
      uvMapUrl: dataUrl,
      uvMap: `${selectedObject.name}_auto_uv.png`
    });
    
    toast.success('UV map auto-generated! Export it to create projection content.');
  };

  // Export UV texture for content creation
  const [showSmartUVExport, setShowSmartUVExport] = useState(false);

  const handleExportUVTemplate = () => {
    setShowSmartUVExport(true);
  };

  const handleProjectorImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProjector) return;

    // Helper to apply image to selected projector(s)
    const applyImageToProjectors = (imageData) => {
      if (multiSelectIds.length > 1) {
        multiSelectIds.forEach(selectedId => {
          if (projectors.find(p => p.id === selectedId)) {
            updateProjector(selectedId, { projector_image: imageData, test_pattern_on: false });
          }
        });
        const count = multiSelectIds.filter(id => projectors.find(p => p.id === id)).length;
        toast.success(`Image applied to ${count} projectors`);
      } else {
        updateProjector(selectedProjector.id, { projector_image: imageData, test_pattern_on: false });
        toast.success('Projector image applied');
      }
    };

    // Check if it's a HEIC/HEIF file that needs conversion
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                   file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    
    if (isHeic) {
      toast.info('Converting HEIC image...');
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9
        });
        
        const reader = new FileReader();
        reader.onload = (event) => {
          applyImageToProjectors(event.target.result);
        };
        reader.onerror = () => {
          toast.error('Failed to read converted image');
        };
        const blobToRead = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        reader.readAsDataURL(blobToRead);
      } catch (err) {
        logger.error('HEIC conversion error:', err);
        toast.error('HEIC conversion failed. Please try a different image format.');
      }
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        applyImageToProjectors(event.target.result);
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const exportUVTexture = () => {
    if (!selectedObject?.uvMapUrl) {
      toast.error('No UV map to export');
      return;
    }
    
    const link = document.createElement('a');
    link.download = `${selectedObject.name}_uv_texture.png`;
    link.href = selectedObject.uvMapUrl;
    link.click();
    toast.success('UV texture exported');
  };

  const handleAddLens = () => {
    if (!selectedProjector || !newLens.name) return;
    addCustomLens(selectedProjector.id, newLens);
    setAddLensOpen(false);
    setNewLens({ name: '', throw_ratio_min: 1.0, throw_ratio_max: 2.0, lens_shift_h: 30, lens_shift_v: 60, weight: 0 });
    toast.success('Custom lens added');
  };

  // Handle content selection from ContentBrowser
  const handleContentSelect = (item) => {
    // Images resolve to the default (original) URL. Videos resolve to the
    // optimized MP4 variant — never the raw upload, which may be 4K / 60fps /
    // 50 MB and would tank FPS when fed into a VideoTexture.
    const isVideo = item.file_type === 'video';
    const contentUrl = isVideo
      ? contentApi.getOptimizedUrl(item.id)
      : contentApi.getUrl(item.id);

    if (contentBrowserMode === 'texture' && selectedObject) {
      // Objects only accept image textures for now.
      if (isVideo) {
        toast.error('Object textures only support images — pick an image asset.');
        setContentBrowserOpen(false);
        setContentBrowserMode(null);
        return;
      }
      updateSceneObjectWithMulti(selectedObject.id, {
        textureUrl: contentUrl,
        textureName: item.name,
        contentItemId: item.id
      });
      toast.success(`Texture "${item.name}" applied to model`);
    } else if (contentBrowserMode === 'projector_image' && selectedProjector) {
      if (isVideo && item.optimization_status === 'processing') {
        toast.error('Video is still optimizing — try again in a moment.');
        setContentBrowserOpen(false);
        setContentBrowserMode(null);
        return;
      }
      // Projector media: image OR video. The two fields are mutually
      // exclusive — setting one clears the other so the render path is
      // unambiguous. `projector_media_type` is informational only.
      const updatePayload = isVideo
        ? {
            projector_video: contentUrl,
            projector_image: null,
            projector_media_type: 'video',
            test_pattern_on: false,
            contentItemId: item.id,
            contentItemName: item.name,
          }
        : {
            projector_image: contentUrl,
            projector_video: null,
            projector_media_type: 'image',
            test_pattern_on: false,
            contentItemId: item.id,
            contentItemName: item.name,
          };
      if (multiSelectIds.length > 1) {
        multiSelectIds.forEach(selectedId => {
          if (projectors.find(p => p.id === selectedId)) {
            updateProjector(selectedId, updatePayload);
          }
        });
        toast.success(
          `${isVideo ? 'Video' : 'Image'} "${item.name}" applied to ${multiSelectIds.filter(id => projectors.find(p => p.id === id)).length} projectors`
        );
      } else {
        updateProjector(selectedProjector.id, updatePayload);
        toast.success(`${isVideo ? 'Video' : 'Image'} "${item.name}" applied to projector`);
      }
    } else if (contentBrowserMode === 'led_content' && selectedObject?.type === 'led_screen') {
      updateSceneObjectWithMulti(selectedObject.id, {
        ledConfig: { ...selectedObject.ledConfig, contentItemId: item.id, contentUrl: contentUrl }
      });
      toast.success(`Content "${item.name}" assigned to LED Screen`);
    }

    setContentBrowserOpen(false);
    setContentBrowserMode(null);
  };

  // Open content browser for texture selection
  const openTextureContentBrowser = () => {
    setContentBrowserMode('texture');
    setContentBrowserOpen(true);
  };

  // Open content browser for projector image selection
  const openProjectorImageContentBrowser = () => {
    setContentBrowserMode('projector_image');
    setContentBrowserOpen(true);
  };

  // Multi-select helpers (state now in AppContext)
  const [measureMode, setMeasureMode] = useState(false);
  const [measureStart, setMeasureStart] = useState(null);
  const [measureEnd, setMeasureEnd] = useState(null);

  const toggleMultiSelect = useCallback((id) => {
    setMultiSelectIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  }, [setMultiSelectIds]);

  const isMultiSelected = useCallback((id) => multiSelectIds.includes(id), [multiSelectIds]);

  const handleMultiDelete = useCallback(() => {
    if (multiSelectIds.length === 0) return;
    
    multiSelectIds.forEach(id => {
      if (projectors.find(p => p.id === id)) {
        removeProjector(id);
      } else {
        removeSceneObject(id);
      }
    });
    setMultiSelectIds([]);
    toast.success(`Deleted ${multiSelectIds.length} items`);
  }, [multiSelectIds, projectors, removeProjector, removeSceneObject]);

  // Wrapper function to update projector(s) - applies to all selected if multi-select active.
  // For position/rotation: converts ABSOLUTE input → DELTA so multi-selected items keep
  // their relative spacing (matches gizmo behavior). For all other fields: applies the
  // same value to every selected (e.g., toggling beam color/lens type in bulk).
  const updateProjectorWithMulti = useCallback((id, updates) => {
    if (multiSelectIds.length > 1) {
      const idsToUpdate = new Set(multiSelectIds);
      idsToUpdate.add(id);
      const ref = projectors.find(p => p.id === id);

      // Pre-compute deltas vs the input's reference projector (the one being edited).
      let posDelta = null, rotDelta = null;
      if (ref && updates.position && typeof updates.position === 'object') {
        const cur = ref.position || { x: 0, y: 0, z: 0 };
        posDelta = {
          x: (updates.position.x ?? cur.x) - (cur.x ?? 0),
          y: (updates.position.y ?? cur.y) - (cur.y ?? 0),
          z: (updates.position.z ?? cur.z) - (cur.z ?? 0),
        };
      }
      if (ref && updates.rotation && typeof updates.rotation === 'object') {
        const cur = ref.rotation || { x: 0, y: 0, z: 0 };
        rotDelta = {
          x: (updates.rotation.x ?? cur.x) - (cur.x ?? 0),
          y: (updates.rotation.y ?? cur.y) - (cur.y ?? 0),
          z: (updates.rotation.z ?? cur.z) - (cur.z ?? 0),
        };
      }

      idsToUpdate.forEach(selectedId => {
        const p = projectors.find(pp => pp.id === selectedId);
        if (!p) return;
        const perItem = { ...updates };
        if (posDelta) {
          if (selectedId === id) {
            perItem.position = updates.position; // reference uses the typed value verbatim
          } else {
            const cur = p.position || { x: 0, y: 0, z: 0 };
            perItem.position = {
              x: (cur.x || 0) + posDelta.x,
              y: (cur.y || 0) + posDelta.y,
              z: (cur.z || 0) + posDelta.z,
            };
          }
        }
        if (rotDelta) {
          if (selectedId === id) {
            perItem.rotation = updates.rotation;
          } else {
            const cur = p.rotation || { x: 0, y: 0, z: 0 };
            perItem.rotation = {
              x: (cur.x || 0) + rotDelta.x,
              y: (cur.y || 0) + rotDelta.y,
              z: (cur.z || 0) + rotDelta.z,
            };
          }
        }
        updateProjector(selectedId, perItem);
      });
    } else {
      updateProjector(id, updates);
    }
  }, [multiSelectIds, projectors, updateProjector]);

  // Wrapper function to update object(s) - applies to all selected if multi-select active.
  // Same delta logic as projectors above for position/rotation.
  const updateSceneObjectWithMulti = useCallback((id, updates) => {
    if (multiSelectIds.length > 1) {
      const idsToUpdate = new Set(multiSelectIds);
      idsToUpdate.add(id);
      const ref = sceneObjects.find(o => o.id === id);

      let posDelta = null, rotDelta = null;
      if (ref && updates.position && typeof updates.position === 'object') {
        const cur = ref.position || { x: 0, y: 0, z: 0 };
        posDelta = {
          x: (updates.position.x ?? cur.x) - (cur.x ?? 0),
          y: (updates.position.y ?? cur.y) - (cur.y ?? 0),
          z: (updates.position.z ?? cur.z) - (cur.z ?? 0),
        };
      }
      if (ref && updates.rotation && typeof updates.rotation === 'object') {
        const cur = ref.rotation || { x: 0, y: 0, z: 0 };
        rotDelta = {
          x: (updates.rotation.x ?? cur.x) - (cur.x ?? 0),
          y: (updates.rotation.y ?? cur.y) - (cur.y ?? 0),
          z: (updates.rotation.z ?? cur.z) - (cur.z ?? 0),
        };
      }

      idsToUpdate.forEach(selectedId => {
        const o = sceneObjects.find(oo => oo.id === selectedId);
        if (!o) return;
        const perItem = { ...updates };
        if (posDelta) {
          if (selectedId === id) {
            perItem.position = updates.position;
          } else {
            const cur = o.position || { x: 0, y: 0, z: 0 };
            perItem.position = {
              x: (cur.x || 0) + posDelta.x,
              y: (cur.y || 0) + posDelta.y,
              z: (cur.z || 0) + posDelta.z,
            };
          }
        }
        if (rotDelta) {
          if (selectedId === id) {
            perItem.rotation = updates.rotation;
          } else {
            const cur = o.rotation || { x: 0, y: 0, z: 0 };
            perItem.rotation = {
              x: (cur.x || 0) + rotDelta.x,
              y: (cur.y || 0) + rotDelta.y,
              z: (cur.z || 0) + rotDelta.z,
            };
          }
        }
        updateSceneObject(selectedId, perItem);
      });
    } else {
      updateSceneObject(id, updates);
    }
  }, [multiSelectIds, sceneObjects, updateSceneObject]);

  const handleMultiDuplicate = useCallback(() => {
    if (multiSelectIds.length === 0) return;
    duplicateMultiSelected(multiSelectIds);
  }, [multiSelectIds, duplicateMultiSelected]);

  const selectAllObjects = useCallback(() => {
    const allIds = [...sceneObjects.map(o => o.id), ...projectors.map(p => p.id)];
    setMultiSelectIds(allIds);
  }, [sceneObjects, projectors]);

  const clearMultiSelect = useCallback(() => {
    setMultiSelectIds([]);
  }, []);

  // Solo/Mute functions
  const handleSoloObject = useCallback((id, isProjector) => {
    // Hide all other objects, show only this one
    sceneObjects.forEach(obj => {
      updateSceneObject(obj.id, { visible: obj.id === id });
    });
    projectors.forEach(proj => {
      updateProjector(proj.id, { power_on: proj.id === id });
    });
    toast.success('Solo mode enabled');
  }, [sceneObjects, projectors, updateSceneObject, updateProjector]);

  const handleShowAll = useCallback(() => {
    sceneObjects.forEach(obj => {
      updateSceneObject(obj.id, { visible: true });
    });
    projectors.forEach(proj => {
      updateProjector(proj.id, { power_on: true });
    });
    toast.success('All objects visible');
  }, [sceneObjects, projectors, updateSceneObject, updateProjector]);

  // Calculate distance between two objects
  const calculateDistance = useCallback((obj1, obj2) => {
    if (!obj1?.position || !obj2?.position) return null;
    const dx = obj1.position.x - obj2.position.x;
    const dy = obj1.position.y - obj2.position.y;
    const dz = obj1.position.z - obj2.position.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }, []);

  const allLenses = selectedProjector ? [
    ...(selectedProjector.spec?.lenses || []),
    ...(selectedProjector.custom_lenses || [])
  ] : [];

  return (
    <>
    <div className="side-panel side-panel-right w-80 flex flex-col" data-testid="properties-panel">
      <div className="panel-header flex-shrink-0">
        <h3>Properties</h3>
      </div>
      
      <Tabs value={activeRightTab === 'scene' ? 'properties' : activeRightTab} onValueChange={setActiveRightTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-2 mt-2 bg-zinc-800 grid grid-cols-4 flex-shrink-0">
          <TabsTrigger value="properties" className="text-xs">Properties</TabsTrigger>
          <TabsTrigger value="multi" className="text-xs" data-testid="multi-projection-tab">Multi Proj</TabsTrigger>
          <TabsTrigger value="camera" className="text-xs" data-testid="camera-tab-trigger">Camera</TabsTrigger>
          <TabsTrigger value="settings" className="text-xs">Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="properties" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3">
            {/* Multi-Select Indicator - Simple */}
            {multiSelectIds.length > 1 && !editMode && (
              <div className="mb-3 px-3 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium text-fuchsia-400">
                  {multiSelectIds.length} items selected
                </span>
                <Button variant="ghost" size="sm" className="h-6 text-xs"
                  onClick={() => setMultiSelectIds([])}>
                  Clear
                </Button>
              </div>
            )}
            {/* Mesh Editor Mode */}
            {editMode ? (
              <div className="space-y-3">
                {/* Mesh Editor Header */}
                <div className="flex items-center justify-between bg-amber-500/20 -mx-3 -mt-3 px-3 py-2 border-b border-amber-500/50">
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-400">Mesh Editor</span>
                  </div>
                  <span className="text-xs text-zinc-400">
                    {editModeTab === 'vertices' 
                      ? `${selectedVertices.length} / ${vertexList.length} vertices` 
                      : `${selectedComponents.length} / ${meshComponents.length} components`}
                  </span>
                </div>

                {/* Vertices / Components Tab Toggle */}
                <div className="flex items-center gap-1 bg-zinc-800 rounded p-0.5">
                  <Button
                    variant={editModeTab === 'vertices' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs flex-1"
                    onClick={() => setEditModeTab('vertices')}
                  >
                    <Grid3X3 className="w-3 h-3 mr-1" />
                    Vertices
                  </Button>
                  <Button
                    variant={editModeTab === 'components' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs flex-1"
                    onClick={() => setEditModeTab('components')}
                  >
                    <Layers className="w-3 h-3 mr-1" />
                    Components
                  </Button>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wide">View Mode</span>
                  <div className="flex items-center gap-1 bg-zinc-800 rounded p-0.5">
                    <Button
                      variant={viewMode === 'wireframe' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={() => setViewMode('wireframe')}
                      data-testid="wireframe-mode-btn"
                    >
                      <Grid2X2 className="w-3 h-3 mr-1" />
                      Wire
                    </Button>
                    <Button
                      variant={viewMode === 'solid' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={() => setViewMode('solid')}
                      data-testid="solid-mode-btn"
                    >
                      <BoxSelect className="w-3 h-3 mr-1" />
                      Solid
                    </Button>
                  </div>
                </div>

                <Separator className="bg-zinc-700" />

                {/* Undo/Redo - Common for both tabs */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wide">History</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleUndo(); }}
                      disabled={undoStack.length === 0}
                      title="Undo (Ctrl+Z)"
                      data-testid="mesh-editor-undo-btn"
                    >
                      <Undo2 className="w-3 h-3 mr-1" />
                      Undo
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleRedo(); }}
                      disabled={redoStack.length === 0}
                      title="Redo (Ctrl+Shift+Z)"
                      data-testid="mesh-editor-redo-btn"
                    >
                      <Redo2 className="w-3 h-3 mr-1" />
                      Redo
                    </Button>
                  </div>
                </div>

                <Separator className="bg-zinc-700" />

                {/* VERTICES TAB CONTENT */}
                {editModeTab === 'vertices' && (
                  <>
                    {/* Quick Actions */}
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleSelectAllVertices}
                        data-testid="select-all-vertices-btn"
                      >
                        <CheckSquare className="w-3 h-3 mr-1" />
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={clearVertexSelection}
                        data-testid="clear-vertex-selection-btn"
                      >
                        <Square className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                  
                  {/* Section Selection Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-blue-400 hover:text-blue-300"
                        data-testid="select-section-btn"
                      >
                        <Layers className="w-3 h-3 mr-1" />
                        Section
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-zinc-900 border-zinc-700">
                      <DropdownMenuItem onClick={() => handleSelectSection('top')} className="text-xs">
                        Top Half
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSelectSection('bottom')} className="text-xs">
                        Bottom Half
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleSelectSection('front')} className="text-xs">
                        Front Half
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSelectSection('back')} className="text-xs">
                        Back Half
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleSelectSection('left')} className="text-xs">
                        Left Half
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSelectSection('right')} className="text-xs">
                        Right Half
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleSelectSection('topQuarter')} className="text-xs">
                        Top Quarter
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSelectSection('bottomQuarter')} className="text-xs">
                        Bottom Quarter
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSelectSection('center')} className="text-xs">
                        Center Region
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-amber-400 hover:text-amber-300"
                    onClick={handleHideSelectedVertices}
                    disabled={selectedVertices.length === 0}
                    data-testid="hide-selected-vertices-btn"
                  >
                    <EyeOff className="w-3 h-3 mr-1" />
                    Hide ({selectedVertices.length})
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-400 hover:text-red-300"
                    onClick={handleDeleteSelectedVertices}
                    disabled={selectedVertices.length === 0}
                    data-testid="delete-selected-vertices-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete ({selectedVertices.length})
                  </Button>
                  {hiddenVertices.size > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-green-400 hover:text-green-300"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleUnhideAllVertices(); }}
                      data-testid="unhide-all-btn"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Show All ({hiddenVertices.size})
                    </Button>
                  )}
                </div>
                
                <p className="text-[10px] text-zinc-600">Ctrl+Click to toggle, Shift+Click for range selection.</p>

                <Separator className="bg-zinc-700" />

                {/* Vertex List - TALLER */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
                      Vertices ({visibleVertexList.length} visible / {vertexList.length} total)
                    </span>
                  </div>
                  <ScrollArea className="h-[300px] -mx-1">
                    <div className="space-y-1 px-1">
                      {visibleVertexList.map((vertex) => (
                        <div
                          key={vertex.key}
                          className={`
                            flex items-center justify-between px-2 py-1.5 rounded cursor-pointer text-xs group
                            ${selectedVertices.includes(vertex.index) ? 'bg-yellow-500/30 border border-yellow-500/50' : 'hover:bg-zinc-800 border border-transparent'}
                          `}
                          onClick={(e) => handleVertexClick(vertex.index, e)}
                          data-testid={`vertex-item-${vertex.index}`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {selectedVertices.includes(vertex.index) ? (
                              <CheckSquare className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                            ) : (
                              <Square className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                            )}
                            <span className="text-zinc-300 truncate">{vertex.name}</span>
                            <span className="text-[10px] text-zinc-600 font-mono">
                              ({vertex.x}, {vertex.y}, {vertex.z})
                            </span>
                          </div>
                          
                          {/* Hide/Delete buttons - right side */}
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHideVertex(vertex.index);
                              }}
                              title="Hide"
                            >
                              <EyeOff className="w-3 h-3 text-zinc-400" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-400 hover:text-red-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVertex(vertex.index);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                  </>
                )}

                {/* COMPONENTS TAB CONTENT */}
                {editModeTab === 'components' && (
                  <>
                    {/* Component Quick Actions */}
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleSelectAllComponents}
                      >
                        <CheckSquare className="w-3 h-3 mr-1" />
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleClearComponentSelection}
                      >
                        <Square className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-yellow-400 hover:text-yellow-300"
                        onClick={handleHideSelectedComponents}
                        disabled={selectedComponents.length === 0}
                        data-testid="hide-selected-components-btn"
                      >
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hide ({selectedComponents.length})
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-400 hover:text-red-300"
                        onClick={handleDeleteSelectedComponents}
                        disabled={selectedComponents.length === 0}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete ({selectedComponents.length})
                      </Button>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Component List */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
                          Components ({meshComponents.filter(c => !hiddenComponents.has(c.id)).length} visible / {meshComponents.length} total)
                        </span>
                      </div>
                      <ScrollArea className="h-[300px] -mx-1">
                        <div className="space-y-0.5 px-1">
                          {meshComponents.length === 0 ? (
                            <div className="text-center text-zinc-500 text-xs py-4">
                              No components found. This mesh has a single geometry.
                            </div>
                          ) : (
                            <>
                              {/* Model Root */}
                              <div className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400">
                                <ChevronRight className="w-3 h-3" />
                                <Box className="w-3 h-3" />
                                <span className="font-medium">Model</span>
                                <span className="text-[10px] text-zinc-600 ml-auto">
                                  {meshComponents.length} parts
                                </span>
                              </div>
                              
                              {/* Component List */}
                              {meshComponents.filter(c => !hiddenComponents.has(c.id)).map((component, idx) => {
                                const isSelected = selectedComponents.includes(component.id);
                                // Generate a color based on component index
                                const colors = ['#00FFFF', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3'];
                                const color = colors[idx % colors.length];
                                
                                return (
                                  <div
                                    key={component.id}
                                    className={`
                                      flex items-center gap-2 px-2 py-1.5 ml-4 rounded cursor-pointer text-xs group transition-all
                                      ${isSelected ? 'bg-cyan-500/30 border border-cyan-500/50' : 'hover:bg-zinc-800/50 border border-transparent'}
                                    `}
                                    onClick={() => handleToggleComponent(component.id)}
                                    data-testid={`component-${idx}`}
                                  >
                                    {/* Checkbox */}
                                    <div className={`w-3 h-3 rounded-sm border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-cyan-500 border-cyan-400' : 'border-zinc-600'}`}>
                                      {isSelected && <Check className="w-2 h-2 text-white" />}
                                    </div>
                                    
                                    {/* Color indicator */}
                                    <div 
                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: color }}
                                    />
                                    
                                    {/* Name */}
                                    <span className={`truncate flex-1 ${isSelected ? 'text-cyan-300 font-medium' : 'text-zinc-300'}`}>
                                      {component.name || `Component_${idx + 1}`}
                                    </span>
                                    
                                    {/* Vertex count */}
                                    <span className="text-[10px] text-zinc-500">
                                      {component.vertexCount?.toLocaleString() || 0}
                                    </span>
                                    
                                    {/* Actions - always visible */}
                                    <div className="flex items-center gap-0.5">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 text-zinc-400 hover:text-yellow-400"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleHideComponent(component.id);
                                        }}
                                        title="Hide component"
                                        data-testid={`hide-component-${component.id}`}
                                      >
                                        <EyeOff className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 text-zinc-400 hover:text-red-400"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteComponent(component.id);
                                        }}
                                        title="Delete component"
                                        data-testid={`delete-component-${component.id}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          
                          {/* Hidden Components Section */}
                          {hiddenComponents.size > 0 && (
                            <>
                              <Separator className="bg-zinc-700 my-2" />
                              <div className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 px-2">
                                Hidden ({hiddenComponents.size})
                              </div>
                              {meshComponents.filter(c => hiddenComponents.has(c.id)).map((component) => (
                                <div
                                  key={component.id}
                                  className="flex items-center gap-2 px-2 py-1 ml-4 rounded cursor-pointer text-xs group opacity-50 hover:opacity-100 hover:bg-zinc-800/50"
                                  onClick={() => handleShowComponent(component.id)}
                                >
                                  <Eye className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                                  <span className="text-zinc-500 truncate">{component.name}</span>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </>
                )}

                <Separator className="bg-zinc-700" />

                {/* Save As & Exit Buttons */}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-8 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                    onClick={() => setSaveAsDialogOpen(true)}
                    data-testid="save-as-library-btn"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Save As to Library
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-8 text-xs border-green-500/50 text-green-400 hover:bg-green-500/20"
                      onClick={() => exitEditMode(true)}
                      data-testid="done-edit-btn"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Done
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-red-400 hover:text-red-300"
                      onClick={handleCancelEditMode}
                      data-testid="cancel-edit-btn"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>

                {/* Save As Dialog */}
                <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
                  <DialogContent className="bg-zinc-900 border-zinc-700">
                    <DialogHeader>
                      <DialogTitle className="text-zinc-100">Save to Library</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Name</Label>
                        <Input
                          value={saveAsName}
                          onChange={(e) => setSaveAsName(e.target.value)}
                          placeholder="Enter name for saved mesh..."
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="save-as-name-input"
                        />
                      </div>
                      <p className="text-xs text-zinc-500">
                        This will save the current edited mesh to your library. Hidden vertices will be excluded.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setSaveAsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSaveAsToLibrary}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="confirm-save-as-btn"
                      >
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            ) : selected ? (
              <div className="space-y-4">
                {/* Object Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isProjector ? (
                      <Projector className="w-4 h-4 text-fuchsia-400" />
                    ) : (
                      <Box className="w-4 h-4 text-zinc-400" />
                    )}
                    <Input
                      value={selected.name}
                      onChange={(e) => isProjector 
                        ? updateProjectorWithMulti(selected.id, { name: e.target.value })
                        : updateSceneObjectWithMulti(selected.id, { name: e.target.value })
                      }
                      className="h-7 text-xs bg-transparent border-none px-1 font-medium"
                      data-testid="object-name-input"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-300"
                    onClick={handleDelete}
                    data-testid="delete-object-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Out of Range Warning */}
                {isProjector && selectedProjector.out_of_range && (
                  <div className="flex items-center gap-2 p-2 bg-red-500/20 border border-red-500/50 rounded text-xs text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Throw ratio outside lens range!</span>
                  </div>
                )}

                <Separator className="bg-zinc-700" />

                {/* Transform Section */}
                <div className="space-y-3">
                  <div className="properties-section-title flex items-center justify-between">
                    <span>Transform</span>
                    <Button
                      variant={selected.locked ? "default" : "ghost"}
                      size="sm"
                      className={`h-5 px-2 text-[10px] ${selected.locked ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'text-zinc-400 hover:text-white'}`}
                      onClick={() => isProjector
                        ? updateProjectorWithMulti(selected.id, { locked: !selected.locked })
                        : updateSceneObjectWithMulti(selected.id, { locked: !selected.locked })
                      }
                      data-testid="transform-lock-btn"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      {selected.locked ? 'Locked' : 'Lock'}
                    </Button>
                  </div>
                  <Vector3Input
                    label={`Position (${projectSettings.unit})`}
                    value={selected.position}
                    onChange={(val) => isProjector
                      ? updateProjectorWithMulti(selected.id, { position: val })
                      : updateSceneObjectWithMulti(selected.id, { position: val })
                    }
                    unit={projectSettings.unit}
                    isPosition={true}
                    step={projectSettings.unit === 'mm' ? 100 : projectSettings.unit === 'cm' ? 10 : 0.1}
                    disabled={selected.locked}
                    onSnapToGround={!isProjector ? () => {
                      if (snapToGroundFnRef?.current) {
                        const newY = snapToGroundFnRef.current(selected.id);
                        if (newY !== null && newY !== undefined) {
                          updateSceneObjectWithMulti(selected.id, {
                            position: { ...selected.position, y: newY }
                          });
                        }
                      }
                    } : undefined}
                  />
                  <Vector3Input
                    label="Rotation (°)"
                    value={selected.rotation}
                    onChange={(val) => isProjector
                      ? updateProjectorWithMulti(selected.id, { rotation: val })
                      : updateSceneObjectWithMulti(selected.id, { rotation: val })
                    }
                    step={1}
                    decimalsOverride={1}
                    disabled={selected.locked}
                  />
                  {!isProjector && selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'truss_system' && (
                    <Vector3Input
                      label="Scale"
                      value={selected.scale}
                      onChange={(val) => {
                        const isCylinderType = selectedObject?.type === 'cylinder' || selectedObject?.type === 'pipe';
                        if (isCylinderType && selectedObject?.dimensions) {
                          // Sync dimensions with scale for cylinder/pipe
                          const oldScale = selected.scale || { x: 1, y: 1, z: 1 };
                          const currentDims = selectedObject.dimensions || { width: 1, height: 1, depth: 1 };
                          const newDims = { ...currentDims };
                          
                          // Update depth based on scale.z change: depth = width * scale.z
                          newDims.depth = (currentDims.width || 1) * (val.z || 1);
                          
                          updateSceneObjectWithMulti(selected.id, { scale: val, dimensions: newDims });
                        } else {
                          updateSceneObjectWithMulti(selected.id, { scale: val });
                        }
                      }}
                      step={0.1}
                      disabled={selected.locked}
                    />
                  )}
                </div>

                {/* Projector Controls */}
                {isProjector && selectedProjector.spec && (
                  <>
                    <Separator className="bg-zinc-700" />
                    
                    {/* Reverse Projector Toggle — flip in-place between Normal
                        and Reverse mode without deleting / re-adding. The
                        amber-tinted card stays visible in BOTH states (normal
                        + reverse) so the user always knows what the toggle
                        does. Name is auto-flipped only when the projector
                        still has the default "PR-NNN" / "RPR-NNN" name; any
                        custom name is preserved. */}
                    <div className={`flex items-center gap-2 p-2 rounded-lg border ${
                      selectedProjector.is_reverse_projector
                        ? 'bg-amber-900/30 border-amber-500/30'
                        : 'bg-zinc-900/40 border-zinc-700'
                    }`}>
                      <RotateCcw className={`w-4 h-4 shrink-0 ${selectedProjector.is_reverse_projector ? 'text-amber-400' : 'text-zinc-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-medium ${selectedProjector.is_reverse_projector ? 'text-amber-300' : 'text-zinc-200'}`}>
                          {selectedProjector.is_reverse_projector ? 'Reverse Projector' : 'Normal Projector'}
                        </div>
                        <div className={`text-[10px] ${selectedProjector.is_reverse_projector ? 'text-amber-400/70' : 'text-zinc-500'}`}>
                          {selectedProjector.is_reverse_projector
                            ? "Reveals object's UV-mapped texture"
                            : 'Standard light projection onto surfaces'}
                        </div>
                      </div>
                      <Switch
                        checked={!!selectedProjector.is_reverse_projector}
                        onCheckedChange={(val) => {
                          // Auto-flip the name prefix ONLY when it still matches
                          // the default `PR-NNN` / `RPR-NNN` pattern. A user-renamed
                          // projector ("Stage Right 003", etc.) keeps its name.
                          const nm = selectedProjector.name || '';
                          let nextName = nm;
                          const prMatch = nm.match(/^PR(-\d+)$/i);
                          const rprMatch = nm.match(/^RPR(-\d+)$/i);
                          if (val && prMatch) nextName = `RPR${prMatch[1]}`;
                          else if (!val && rprMatch) nextName = `PR${rprMatch[1]}`;
                          updateProjectorWithMulti(selected.id, {
                            is_reverse_projector: val,
                            name: nextName,
                          });
                        }}
                        data-testid="reverse-projector-toggle"
                      />
                    </div>

                    {/* Power & Controls */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Controls</div>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-2">
                          <Power className="w-3 h-3" /> Power
                        </Label>
                        <Switch
                          checked={selectedProjector.power_on}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { power_on: val })}
                          data-testid="power-toggle"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-2">
                          <Grid3X3 className="w-3 h-3" /> Test Pattern
                        </Label>
                        <Switch
                          checked={selectedProjector.test_pattern_on}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { test_pattern_on: val })}
                          data-testid="test-pattern-toggle"
                        />
                      </div>

                      {selectedProjector.test_pattern_on && (
                        <Select
                          value={selectedProjector.test_pattern_id}
                          onValueChange={(val) => updateProjectorWithMulti(selected.id, { test_pattern_id: val })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="test-pattern-select">
                            <SelectValue placeholder="Select pattern" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                            {projectorLibrary.test_patterns?.map(pattern => (
                              <SelectItem key={pattern.id} value={pattern.id} className="text-xs">
                                {pattern.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-2">
                          <Frame className="w-3 h-3" /> Border
                        </Label>
                        <Switch
                          checked={selectedProjector.show_border}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_border: val })}
                          data-testid="border-toggle"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Show Beam</Label>
                        <Switch
                          checked={selectedProjector.show_beam}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_beam: val })}
                          data-testid="show-beam-toggle"
                        />
                      </div>

                      {selectedProjector.show_beam && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs text-zinc-400">Beam Opacity</Label>
                            <span className="font-mono text-xs">{((selectedProjector.beamOpacity || 0.2) * 100).toFixed(1)}%</span>
                          </div>
                          <Slider
                            value={[Math.round((selectedProjector.beamOpacity || 0.2) * 1000)]}
                            min={1}
                            max={500}
                            step={1}
                            onValueChange={([val]) => updateProjectorWithMulti(selected.id, { beamOpacity: val / 1000 })}
                            data-testid="beam-opacity-slider"
                          />
                          <div className="flex items-center justify-between pt-1">
                            <Label className="text-xs text-zinc-400">Beam Edges (wireframe)</Label>
                            <Switch
                              checked={selectedProjector.show_beam_edges !== false}
                              onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_beam_edges: val })}
                              data-testid="beam-edges-switch"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-zinc-400">Center Line + Distance</Label>
                            <Switch
                              checked={selectedProjector.show_center_line === true}
                              onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_center_line: val })}
                              data-testid="beam-center-line-switch"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-2">
                          {selectedProjector.show_name === false ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} Show Name
                        </Label>
                        <Switch
                          checked={selectedProjector.show_name !== false}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_name: val })}
                          data-testid="show-name-toggle"
                        />
                      </div>
                      
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Locked
                        </Label>
                        <Switch
                          checked={selectedProjector.locked}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { locked: val })}
                          data-testid="projector-locked-toggle"
                        />
                      </div>
                    </div>

                    {/* TEXTURE CONTROL — normal projectors only (hidden for reverse projectors).
                        Values are stored on the projector object via updateProjectorWithMulti
                        and applied to the projector's DynamicTexture canvas (2D draw transform)
                        — the projection shader/material/scene logic is NOT touched. */}
                    {!selectedProjector.is_reverse_projector && (() => {
                      const sx = selectedProjector.textureScaleX ?? 100;
                      const sy = selectedProjector.textureScaleY ?? 100;
                      const ox = selectedProjector.textureOffsetX ?? 0;
                      const oy = selectedProjector.textureOffsetY ?? 0;
                      const step = selectedProjector.textureShiftStep ?? 5;
                      const clamp01 = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
                      const setField = (k, v) => updateProjectorWithMulti(selected.id, { [k]: v });
                      const nudge = (dx, dy) => updateProjectorWithMulti(selected.id, {
                        textureOffsetX: clamp01(Math.round((ox + dx * step) * 100) / 100, -100, 100),
                        textureOffsetY: clamp01(Math.round((oy + dy * step) * 100) / 100, -100, 100),
                      });
                      const resetAll = () => updateProjectorWithMulti(selected.id, {
                        textureScaleX: 100, textureScaleY: 100, textureOffsetX: 0, textureOffsetY: 0,
                      });

                      return (
                        <>
                          <Separator className="bg-zinc-700" />
                          <div className="space-y-3" data-testid="projector-texture-control">
                            <div className="properties-section-title flex items-center justify-between">
                              <span className="flex items-center gap-2">
                                <Image className="w-3 h-3" /> Texture Control
                              </span>
                              <Button
                                variant="ghost" size="sm"
                                className="h-6 px-2 text-[10px] text-zinc-400 hover:text-white"
                                onClick={resetAll}
                                data-testid="texture-control-reset"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Reset
                              </Button>
                            </div>

                            <TextureSliderRow label="Scale X" value={sx} min={0} max={500} suffix="%" field="textureScaleX" testId="texture-scale-x" onCommit={setField} />
                            <TextureSliderRow label="Scale Y" value={sy} min={0} max={500} suffix="%" field="textureScaleY" testId="texture-scale-y" onCommit={setField} />
                            <TextureSliderRow label="Offset X" value={ox} min={-100} max={100} suffix="%" field="textureOffsetX" testId="texture-offset-x" onCommit={setField} />
                            <TextureSliderRow label="Offset Y" value={oy} min={-100} max={100} suffix="%" field="textureOffsetY" testId="texture-offset-y" onCommit={setField} />

                            {/* Shift D-pad */}
                            <div className="space-y-2 pt-1">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                                  <Move className="w-3 h-3" /> Shift
                                </Label>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-zinc-500">step</span>
                                  <Input
                                    type="number"
                                    value={step}
                                    min={0.1}
                                    max={50}
                                    step={0.5}
                                    className="h-6 w-14 text-xs bg-zinc-900 text-center"
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value);
                                      if (!isNaN(v)) setField('textureShiftStep', clamp01(v, 0.1, 50));
                                    }}
                                    data-testid="texture-shift-step-input"
                                  />
                                  <span className="text-[10px] text-zinc-500">%</span>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-1 max-w-[140px] mx-auto">
                                <div></div>
                                <Button variant="outline" size="sm"
                                  className="h-8 w-full p-0 bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                                  onClick={() => nudge(0, 1)}
                                  data-testid="texture-shift-up"
                                ><ArrowUp className="w-4 h-4" /></Button>
                                <div></div>

                                <Button variant="outline" size="sm"
                                  className="h-8 w-full p-0 bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                                  onClick={() => nudge(-1, 0)}
                                  data-testid="texture-shift-left"
                                ><ArrowLeft className="w-4 h-4" /></Button>
                                <Button variant="outline" size="sm"
                                  className="h-8 w-full p-0 bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-400"
                                  onClick={() => updateProjectorWithMulti(selected.id, { textureOffsetX: 0, textureOffsetY: 0 })}
                                  data-testid="texture-shift-center"
                                  title="Recenter offset"
                                ><Maximize2 className="w-3 h-3" /></Button>
                                <Button variant="outline" size="sm"
                                  className="h-8 w-full p-0 bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                                  onClick={() => nudge(1, 0)}
                                  data-testid="texture-shift-right"
                                ><ArrowRight className="w-4 h-4" /></Button>

                                <div></div>
                                <Button variant="outline" size="sm"
                                  className="h-8 w-full p-0 bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                                  onClick={() => nudge(0, -1)}
                                  data-testid="texture-shift-down"
                                ><ArrowDown className="w-4 h-4" /></Button>
                                <div></div>
                              </div>
                              <div className="text-[10px] text-zinc-500 text-center">
                                Each arrow nudges Offset by ±{step}%
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}

                    <Separator className="bg-zinc-700" />
                    
                    {/* Lens Selection */}
                    <div className="space-y-3">
                      <div className="properties-section-title flex items-center justify-between">
                        <span>Lens</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => setAddLensOpen(true)}
                          data-testid="add-lens-btn"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      
                      <Select
                        value={selectedProjector.selected_lens_id || ''}
                        onValueChange={(val) => updateProjectorWithMulti(selected.id, { selected_lens_id: val })}
                      >
                        <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="lens-select">
                          <SelectValue placeholder="Select lens" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                          {allLenses.map(lens => (
                            <SelectItem key={lens.id} value={lens.id} className="text-xs">
                              {lens.name} ({lens.throw_ratio_min}-{lens.throw_ratio_max})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {selectedProjector.selected_lens && (
                        <div className="text-[10px] text-zinc-500 space-y-0.5">
                          <div>Ratio: {selectedProjector.selected_lens.throw_ratio_min} - {selectedProjector.selected_lens.throw_ratio_max}</div>
                          {selectedProjector.selected_lens.lens_shift_h !== undefined && (
                            <div>Shift H: {selectedProjector.selected_lens.lens_shift_h}% | V: {selectedProjector.selected_lens.lens_shift_v}%</div>
                          )}
                          {selectedProjector.selected_lens.weight > 0 && (
                            <div>Weight: {selectedProjector.selected_lens.weight} kg</div>
                          )}
                        </div>
                      )}
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Lens Type (Standard / 90° / Top-Bottom) */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Lens Type</div>
                      <Select
                        value={selectedProjector.lensType || 'standard'}
                        onValueChange={(val) => updateProjectorWithMulti(selected.id, { lensType: val })}
                      >
                        <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="lens-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="standard" className="text-xs">Standard</SelectItem>
                          <SelectItem value="lens90" className="text-xs">90° Lens</SelectItem>
                          <SelectItem value="topBottom" className="text-xs">Periscope</SelectItem>
                          <SelectItem value="ust" className="text-xs">UST Lens</SelectItem>
                        </SelectContent>
                      </Select>

                      {selectedProjector.lensType === 'lens90' && (
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-400">Lens Rotation</Label>
                          <Select
                            value={selectedProjector.lens90Rotation || 'top'}
                            onValueChange={(val) => updateProjectorWithMulti(selected.id, { lens90Rotation: val })}
                          >
                            <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="lens90-rotation-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="left" className="text-xs">Left</SelectItem>
                              <SelectItem value="right" className="text-xs">Right</SelectItem>
                              <SelectItem value="top" className="text-xs">Top</SelectItem>
                              <SelectItem value="bottom" className="text-xs">Bottom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {selectedProjector.lensType === 'topBottom' && (
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-400">Lens Mount</Label>
                          <Select
                            value={selectedProjector.topBottomDirection || 'top'}
                            onValueChange={(val) => updateProjectorWithMulti(selected.id, { topBottomDirection: val })}
                          >
                            <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="topbottom-direction-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="top" className="text-xs">Top mount (lens above body)</SelectItem>
                              <SelectItem value="bottom" className="text-xs">Bottom mount (lens below body)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-zinc-500 leading-relaxed">
                            Periscope folds back over the projector — lens cube
                            faces backward and the beam projects to the rear of
                            the projector. Use a low throw ratio (≈ 0.27 – 0.40)
                            for the steep UST projection geometry.
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Throw Ratio & Distance */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Throw Settings</div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-zinc-400">Throw Ratio</Label>
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={parseFloat((selectedProjector.throw_ratio || 1.5).toFixed(2))}
                            key={`tr-${selected.id}-${parseFloat((selectedProjector.throw_ratio || 1.5).toFixed(2))}`}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value);
                              if (isNaN(val) || val <= 0) { e.target.value = (selectedProjector.throw_ratio || 1.5).toFixed(2); return; }
                              updateProjectorWithMulti(selected.id, { throw_ratio: val });
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className="w-20 h-6 text-xs font-mono bg-zinc-900 text-right"
                            data-testid="throw-ratio-input"
                          />
                        </div>
                        <Slider
                          value={[selectedProjector.throw_ratio]}
                          min={selectedProjector.selected_lens?.throw_ratio_min || selectedProjector.spec.throw_ratio_min}
                          max={selectedProjector.selected_lens?.throw_ratio_max || selectedProjector.spec.throw_ratio_max}
                          step={0.01}
                          onValueChange={([val]) => updateProjectorWithMulti(selected.id, { throw_ratio: val })}
                          data-testid="throw-ratio-slider"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-zinc-400">Throw Distance ({projectSettings.unit})</Label>
                          <div className="flex items-center gap-1">
                            <ThrowDistanceInput
                              value={selectedProjector.throw_distance || 5}
                              onChange={(val) => {
                                const updates = { throw_distance: val };
                                // If width is locked, auto-adjust throw ratio to maintain image width
                                if (selectedProjector.width_locked && selectedProjector.locked_width) {
                                  const newRatio = val / selectedProjector.locked_width;
                                  const minRatio = selectedProjector.selected_lens?.throw_ratio_min || selectedProjector.spec?.throw_ratio_min || 0.3;
                                  const maxRatio = selectedProjector.selected_lens?.throw_ratio_max || selectedProjector.spec?.throw_ratio_max || 10;
                                  updates.throw_ratio = Math.min(maxRatio, Math.max(minRatio, newRatio));
                                }
                                updateProjectorWithMulti(selected.id, updates);
                              }}
                              unit={projectSettings.unit}
                            />
                          </div>
                        </div>
                        <Slider
                          value={[metersToUnit(selectedProjector.throw_distance || 5, projectSettings.unit)]}
                          min={metersToUnit(0.5, projectSettings.unit)}
                          max={metersToUnit(200, projectSettings.unit)}
                          step={projectSettings.unit === 'mm' ? 100 : projectSettings.unit === 'cm' ? 10 : 0.1}
                          onValueChange={([val]) => {
                            const distMeters = unitToMeters(val, projectSettings.unit);
                            const updates = { throw_distance: distMeters };
                            if (selectedProjector.width_locked && selectedProjector.locked_width) {
                              const newRatio = distMeters / selectedProjector.locked_width;
                              const minRatio = selectedProjector.selected_lens?.throw_ratio_min || selectedProjector.spec?.throw_ratio_min || 0.3;
                              const maxRatio = selectedProjector.selected_lens?.throw_ratio_max || selectedProjector.spec?.throw_ratio_max || 10;
                              updates.throw_ratio = Math.min(maxRatio, Math.max(minRatio, newRatio));
                            }
                            updateProjectorWithMulti(selected.id, updates);
                          }}
                          data-testid="throw-distance-slider"
                        />
                      </div>

                      {/* Image Width — editable + lockable */}
                      <ImageWidthInput
                        selectedProjector={selectedProjector}
                        projectSettings={projectSettings}
                        selectedId={selected.id}
                        updateProjectorWithMulti={updateProjectorWithMulti}
                      />
                    </div>

                    {/* Projection Calculator - Shows calculated projection size */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 space-y-2">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-fuchsia-400" />
                        <span className="text-xs font-medium text-zinc-300">Projection Calculator</span>
                      </div>
                      
                      {/* Formula explanation */}
                      <div className="text-[10px] text-zinc-500 bg-zinc-900/50 p-2 rounded font-mono">
                        Width = Distance ÷ Throw Ratio
                      </div>
                      
                      {/* Calculation inputs summary */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Throw Distance:</span>
                          <span className="font-mono text-zinc-300">
                            {metersToUnit(selectedProjector.throw_distance || 5, safeUnit).toFixed(safeUnitConfig.decimals)}{safeUnit}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Throw Ratio:</span>
                          <span className="font-mono text-zinc-300">{selectedProjector.throw_ratio?.toFixed(2) || '1.50'}</span>
                        </div>
                      </div>
                      
                      {/* Calculated projection size */}
                      <div className="border-t border-zinc-700 pt-2 space-y-1">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Projected Image Size</div>
                        {(() => {
                          const throwDistance = selectedProjector.throw_distance || 5;
                          const throwRatio = selectedProjector.throw_ratio || 1.5;
                          const aspectRatio = selectedProjector.aspect_ratio || '16:10';
                          const [aw, ah] = aspectRatio.split(':').map(Number);
                          const aspectValue = aw / ah;
                          
                          // Calculate projected image dimensions
                          const projectedWidth = throwDistance / throwRatio;
                          const projectedHeight = projectedWidth / aspectValue;
                          const projectedDiagonal = Math.sqrt(projectedWidth * projectedWidth + projectedHeight * projectedHeight);
                          const projectedArea = projectedWidth * projectedHeight;
                          
                          return (
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-zinc-900 p-2 rounded">
                                  <div className="text-[10px] text-zinc-500">Width</div>
                                  <div className="font-mono text-sm text-fuchsia-400">
                                    {metersToUnit(projectedWidth, safeUnit).toFixed(safeUnitConfig.decimals)}{safeUnit}
                                  </div>
                                </div>
                                <div className="bg-zinc-900 p-2 rounded">
                                  <div className="text-[10px] text-zinc-500">Height</div>
                                  <div className="font-mono text-sm text-fuchsia-400">
                                    {metersToUnit(projectedHeight, safeUnit).toFixed(safeUnitConfig.decimals)}{safeUnit}
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-zinc-900 p-2 rounded">
                                  <div className="text-[10px] text-zinc-500">Diagonal</div>
                                  <div className="font-mono text-xs text-zinc-300">
                                    {metersToUnit(projectedDiagonal, safeUnit).toFixed(safeUnitConfig.decimals)}{safeUnit}
                                  </div>
                                </div>
                                <div className="bg-zinc-900 p-2 rounded">
                                  <div className="text-[10px] text-zinc-500">Area</div>
                                  <div className="font-mono text-xs text-zinc-300">
                                    {projectedArea.toFixed(2)}m²
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Surface Image Size — read-only frustum→surface measurement.
                          Toggle, overlay control, and live values. Does not affect
                          beam, lux, projection shader, or any rendering. */}
                      <SurfaceImageSizePanel
                        projector={selectedProjector}
                        projectorId={selected.id}
                        useSurfaceSize={selectedProjector.useSurfaceImageSize !== false}
                        overlayEnabled={selectedProjector.imageSizeOverlayEnabled === true}
                        projectSettings={projectSettings}
                        onUpdate={(patch) => updateProjectorWithMulti(selected.id, patch)}
                      />

                      {/* Quick reference: What throw ratio means */}
                      <div className="text-[10px] text-zinc-500 italic">
                        Lower ratio = wider angle = larger image at same distance
                      </div>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Aspect Ratio & Image Size */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Image</div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-zinc-400">Aspect Ratio</Label>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-6 w-6 ${selectedProjector.lock_aspect_ratio ? 'text-fuchsia-400' : 'text-zinc-500'}`}
                            onClick={() => updateProjectorWithMulti(selected.id, { lock_aspect_ratio: !selectedProjector.lock_aspect_ratio })}
                            data-testid="lock-aspect-ratio-btn"
                          >
                            {selectedProjector.lock_aspect_ratio ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                          </Button>
                        </div>
                        <Select
                          value={selectedProjector.aspect_ratio}
                          onValueChange={(val) => updateProjectorWithMulti(selected.id, { aspect_ratio: val })}
                        >
                          <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="aspect-ratio-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {Object.keys(ASPECT_RATIOS).map(ratio => (
                              <SelectItem key={ratio} value={ratio} className="text-xs">
                                {ratio}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Resolution Preset Selector */}
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Resolution Preset</Label>
                        <Select
                          value={`${selectedProjector.image_width}x${selectedProjector.image_height}`}
                          onValueChange={(val) => {
                            const [w, h] = val.split('x').map(Number);
                            updateProjectorWithMulti(selected.id, { image_width: w, image_height: h });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="resolution-preset-select">
                            <SelectValue placeholder="Select resolution" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                            {/* Native resolution option */}
                            <SelectItem 
                              value={`${selectedProjector.spec?.resolution_width || 1920}x${selectedProjector.spec?.resolution_height || 1200}`}
                              className="text-xs font-semibold text-fuchsia-400"
                            >
                              Native ({selectedProjector.spec?.resolution_width || 1920}×{selectedProjector.spec?.resolution_height || 1200})
                            </SelectItem>
                            <div className="h-px bg-zinc-700 my-1" />
                            {/* Available presets up to native resolution */}
                            {getAvailableResolutions(
                              selectedProjector.spec?.resolution_width || 1920,
                              selectedProjector.spec?.resolution_height || 1200
                            ).map(preset => (
                              <SelectItem 
                                key={preset.name} 
                                value={`${preset.width}x${preset.height}`}
                                className="text-xs"
                              >
                                {preset.name} ({preset.width}×{preset.height})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[9px] text-zinc-500">
                          Native: {selectedProjector.spec?.resolution_width || 1920}×{selectedProjector.spec?.resolution_height || 1200}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Custom Size</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[10px] text-zinc-500">Width (px)</Label>
                          <Input
                            type="number"
                            value={selectedProjector.image_width || 1920}
                            onChange={(e) => {
                              const newWidth = parseInt(e.target.value) || 1920;
                              const maxWidth = selectedProjector.spec?.resolution_width || 1920;
                              // Cap at native resolution
                              const cappedWidth = Math.min(newWidth, maxWidth);
                              if (newWidth > maxWidth) {
                                toast.warning(`Width capped at native resolution (${maxWidth}px)`);
                              }
                              updateProjectorWithMulti(selected.id, { image_width: cappedWidth });
                            }}
                            max={selectedProjector.spec?.resolution_width || 1920}
                            className="h-7 text-xs font-mono bg-zinc-900"
                            disabled={selectedProjector.lock_image_size}
                            data-testid="image-resolution-width-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-zinc-500">Height (px)</Label>
                          <Input
                            type="number"
                            value={selectedProjector.image_height || 1200}
                            onChange={(e) => {
                              const newHeight = parseInt(e.target.value) || 1200;
                              const maxHeight = selectedProjector.spec?.resolution_height || 1200;
                              // Cap at native resolution
                              const cappedHeight = Math.min(newHeight, maxHeight);
                              if (newHeight > maxHeight) {
                                toast.warning(`Height capped at native resolution (${maxHeight}px)`);
                              }
                              updateProjectorWithMulti(selected.id, { image_height: cappedHeight });
                            }}
                            max={selectedProjector.spec?.resolution_height || 1200}
                            className="h-7 text-xs font-mono bg-zinc-900"
                            disabled={selectedProjector.lock_image_size}
                            data-testid="image-height-input"
                          />
                        </div>
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Lens Shift */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Lens Shift</div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-zinc-400">Horizontal</Label>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              className="w-16 h-6 text-xs font-mono text-right px-1 bg-zinc-900 border-zinc-700"
                              defaultValue={selectedProjector.lens_shift_h}
                              key={`lsh-${selected.id}-${selectedProjector.lens_shift_h}`}
                              min={-(selectedProjector.selected_lens?.lens_shift_h ?? selectedProjector.spec.lens_shift_h)}
                              max={selectedProjector.selected_lens?.lens_shift_h ?? selectedProjector.spec.lens_shift_h}
                              step={1}
                              onBlur={(e) => {
                                const maxH = selectedProjector.selected_lens?.lens_shift_h ?? selectedProjector.spec.lens_shift_h;
                                const parsed = parseFloat(e.target.value);
                                if (isNaN(parsed)) { e.target.value = selectedProjector.lens_shift_h; return; }
                                const val = Math.max(-maxH, Math.min(maxH, parsed));
                                updateProjectorWithMulti(selected.id, { lens_shift_h: val });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                              }}
                              data-testid="lens-shift-h-input"
                            />
                            <span className="text-xs text-zinc-500">%</span>
                          </div>
                        </div>
                        <Slider
                          value={[selectedProjector.lens_shift_h]}
                          min={-(selectedProjector.selected_lens?.lens_shift_h ?? selectedProjector.spec.lens_shift_h)}
                          max={selectedProjector.selected_lens?.lens_shift_h ?? selectedProjector.spec.lens_shift_h}
                          step={1}
                          onValueChange={([val]) => updateProjectorWithMulti(selected.id, { lens_shift_h: val })}
                          data-testid="lens-shift-h-slider"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-zinc-400">Vertical</Label>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              className="w-16 h-6 text-xs font-mono text-right px-1 bg-zinc-900 border-zinc-700"
                              defaultValue={selectedProjector.lens_shift_v}
                              key={`lsv-${selected.id}-${selectedProjector.lens_shift_v}`}
                              min={-(selectedProjector.selected_lens?.lens_shift_v ?? selectedProjector.spec.lens_shift_v)}
                              max={selectedProjector.selected_lens?.lens_shift_v ?? selectedProjector.spec.lens_shift_v}
                              step={1}
                              onBlur={(e) => {
                                const maxV = selectedProjector.selected_lens?.lens_shift_v ?? selectedProjector.spec.lens_shift_v;
                                const parsed = parseFloat(e.target.value);
                                if (isNaN(parsed)) { e.target.value = selectedProjector.lens_shift_v; return; }
                                const val = Math.max(-maxV, Math.min(maxV, parsed));
                                updateProjectorWithMulti(selected.id, { lens_shift_v: val });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                              }}
                              data-testid="lens-shift-v-input"
                            />
                            <span className="text-xs text-zinc-500">%</span>
                          </div>
                        </div>
                        <Slider
                          value={[selectedProjector.lens_shift_v]}
                          min={-(selectedProjector.selected_lens?.lens_shift_v ?? selectedProjector.spec.lens_shift_v)}
                          max={selectedProjector.selected_lens?.lens_shift_v ?? selectedProjector.spec.lens_shift_v}
                          step={1}
                          onValueChange={([val]) => updateProjectorWithMulti(selected.id, { lens_shift_v: val })}
                          data-testid="lens-shift-v-slider"
                        />
                      </div>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Custom Media (image OR video — mutually exclusive) */}
                    <div className="space-y-2">
                      <div className="properties-section-title">Custom Media</div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-zinc-700 hover:border-fuchsia-500"
                        onClick={openProjectorImageContentBrowser}
                        data-testid="select-projector-image-btn"
                      >
                        <FolderOpen className="w-3 h-3 mr-2" />
                        {selectedProjector.projector_video
                          ? 'Change Media (Video)'
                          : selectedProjector.projector_image
                            ? 'Change Media (Image)'
                            : 'Select Image or Video'}
                      </Button>
                      {(selectedProjector.projector_image || selectedProjector.projector_video) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs text-red-400 hover:text-red-300"
                          onClick={() => updateProjectorWithMulti(selected.id, {
                            projector_image: null,
                            projector_video: null,
                            projector_media_type: null,
                          })}
                          data-testid="remove-projector-image-btn"
                        >
                          Remove Media
                        </Button>
                      )}
                      {selectedProjector.projector_video && (
                        <div className="text-[10px] text-fuchsia-400/80 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded px-2 py-1.5 space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                            <span className="font-medium">Video playing (global sync)</span>
                          </div>
                          <p className="text-zinc-500 text-[9px] leading-snug">
                            All projectors pointed at the same clip share one decoder and stay frame-locked.
                          </p>
                        </div>
                      )}
                      <p className="text-[10px] text-zinc-500">
                        Upload images or videos to the Content library first. Videos are auto-optimized to 720p30 MP4.
                      </p>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Brightness & Color */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Output</div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-zinc-400">Brightness</Label>
                          <span className="font-mono text-xs">{selectedProjector.brightness}%</span>
                        </div>
                        <Slider
                          value={[selectedProjector.brightness]}
                          min={0}
                          max={300}
                          step={1}
                          onValueChange={([val]) => updateProjectorWithMulti(selected.id, { brightness: val })}
                          data-testid="brightness-slider"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Beam Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={selectedProjector.color || '#ffffff'}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { color: e.target.value })}
                            className="w-8 h-8 rounded cursor-pointer bg-transparent border border-zinc-700"
                            data-testid="beam-color-picker"
                          />
                          <Input
                            value={selectedProjector.color || '#ffffff'}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { color: e.target.value })}
                            className="h-8 text-xs font-mono"
                            data-testid="beam-color-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Body Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={selectedProjector.body_color || '#4a4a4a'}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { body_color: e.target.value })}
                            className="w-8 h-8 rounded cursor-pointer bg-transparent border border-zinc-700"
                            data-testid="body-color-picker"
                          />
                          <Input
                            value={selectedProjector.body_color || '#4a4a4a'}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { body_color: e.target.value })}
                            className="h-8 text-xs font-mono"
                            data-testid="body-color-input"
                          />
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* ── Projector Stacking (brightness boost mode) ── */}
                    {(() => {
                      const stk = selectedProjector.stacking || { enabled: false, top: 0, bottom: 0, left: 0, right: 0, spacing: 0.5 };
                      const stackCount = 1 + (stk.enabled ? ((stk.top || 0) + (stk.bottom || 0) + (stk.left || 0) + (stk.right || 0)) : 0);
                      const updateStack = (patch) => {
                        const merged = { ...stk, ...patch };
                        updateProjectorWithMulti(selected.id, { stacking: merged });
                      };
                      const StackCounter = ({ label, field, testid }) => (
                        <div className="flex items-center justify-between gap-2">
                          <Label className="text-xs text-zinc-400 w-14">{label}</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              onClick={() => updateStack({ [field]: Math.max(0, (stk[field] || 0) - 1) })}
                              disabled={!stk.enabled}
                              data-testid={`${testid}-dec`}
                            >−</Button>
                            <Input
                              type="number"
                              min={0}
                              max={10}
                              value={stk[field] || 0}
                              onChange={(e) => updateStack({ [field]: Math.max(0, Math.min(10, parseInt(e.target.value) || 0)) })}
                              className="h-7 w-12 text-center text-xs font-mono"
                              disabled={!stk.enabled}
                              data-testid={testid}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              onClick={() => updateStack({ [field]: Math.min(10, (stk[field] || 0) + 1) })}
                              disabled={!stk.enabled}
                              data-testid={`${testid}-inc`}
                            >+</Button>
                          </div>
                        </div>
                      );
                      return (
                        <>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="properties-section-title">Stacking</div>
                              <Switch
                                checked={!!stk.enabled}
                                onCheckedChange={(val) => updateStack({ enabled: val })}
                                data-testid="projector-stacking-toggle"
                              />
                            </div>
                            <div className="text-[10px] text-zinc-500 leading-tight">
                              Stack count: <span className="font-mono text-cyan-400">×{stackCount}</span> · Lux & brightness multiply by total stack count. No extra beams or projections rendered.
                            </div>
                            <div className="space-y-1.5">
                              <StackCounter label="Top" field="top" testid="stack-top" />
                              <StackCounter label="Bottom" field="bottom" testid="stack-bottom" />
                              <StackCounter label="Left" field="left" testid="stack-left" />
                              <StackCounter label="Right" field="right" testid="stack-right" />
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <Label className="text-xs text-zinc-400">Spacing</Label>
                                <span className="font-mono text-xs">{(stk.spacing ?? 0.5).toFixed(2)} m</span>
                              </div>
                              <Slider
                                value={[stk.spacing ?? 0.5]}
                                min={0}
                                max={2}
                                step={0.05}
                                onValueChange={([val]) => updateStack({ spacing: val })}
                                disabled={!stk.enabled}
                                data-testid="stack-spacing-slider"
                              />
                            </div>

                            {/* Stack ghost color override — optional. When unset
                                the ghost inherits the original projector body
                                color so it matches exactly. */}
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs text-zinc-400">Stack Color</Label>
                                <div className="flex items-center gap-1.5">
                                  <Input
                                    type="color"
                                    value={stk.stack_color || selectedProjector.body_color || '#2a2a2a'}
                                    onChange={(e) => updateStack({ stack_color: e.target.value })}
                                    disabled={!stk.enabled}
                                    className="h-7 w-12 p-0.5 cursor-pointer"
                                    data-testid="stack-color-picker"
                                  />
                                  {stk.stack_color && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-[10px]"
                                      onClick={() => updateStack({ stack_color: null })}
                                      disabled={!stk.enabled}
                                      data-testid="stack-color-reset"
                                    >Reset</Button>
                                  )}
                                </div>
                              </div>
                              <div className="text-[10px] text-zinc-500 leading-tight">
                                Override body color for stacked ghost projectors. Leave unset to match original.
                              </div>
                            </div>
                          </div>

                          <Separator className="bg-zinc-700" />
                        </>
                      );
                    })()}

                    {/* Projector Body Size & Frame */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Body & Rigging</div>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Rigging Frame</Label>
                        <Switch
                          checked={selectedProjector.show_frame}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_frame: val })}
                          data-testid="show-frame-toggle"
                        />
                      </div>
                      
                      {selectedProjector.show_frame && (
                        <div className="space-y-1 pl-1">
                          <Label className="text-[10px] text-zinc-500">Frame Weight (kg)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={selectedProjector.frame_weight || selectedProjector.spec?.frame_weight || 5}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { frame_weight: parseFloat(e.target.value) || 0 })}
                            className="h-7 text-xs font-mono bg-zinc-900"
                            data-testid="frame-weight"
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Rigging Clamp</Label>
                        <Switch
                          checked={selectedProjector.show_clamp}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_clamp: val })}
                          data-testid="show-clamp-toggle"
                        />
                      </div>

                      {selectedProjector.show_clamp && (
                        <div className="space-y-1 pl-1">
                          <Label className="text-[10px] text-zinc-500">Clamp Weight (kg)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={selectedProjector.clamp_weight || 2}
                            onChange={(e) => updateProjectorWithMulti(selected.id, { clamp_weight: parseFloat(e.target.value) || 0 })}
                            className="h-7 text-xs font-mono bg-zinc-900"
                            data-testid="clamp-weight"
                          />
                        </div>
                      )}

                      {/* Projector Mount (low-poly ceiling-style stand) ─
                          4-arm cross base + vertical pipe + top plate.
                          Parented to projector so it follows transform. */}
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Projector Mount</Label>
                        <Switch
                          checked={!!selectedProjector.show_mount}
                          onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { show_mount: val })}
                          data-testid="show-mount-toggle"
                        />
                      </div>

                      {selectedProjector.show_mount && (
                        <div className="space-y-3 pl-1">
                          {/* Mount Side — which face of the projector body
                              the cross base attaches to. The pipe always
                              extends OUTWARD from this face. */}
                          <div className="space-y-1">
                            <Label className="text-[10px] text-zinc-500">Mount Side</Label>
                            <div className="grid grid-cols-4 gap-1" data-testid="mount-side">
                              {[
                                { v: 'top',    l: 'Top' },
                                { v: 'bottom', l: 'Bot' },
                                { v: 'left',   l: 'Left' },
                                { v: 'right',  l: 'Right' },
                              ].map((opt) => {
                                const active = (selectedProjector.mount_side || 'top') === opt.v;
                                return (
                                  <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => updateProjectorWithMulti(selected.id, { mount_side: opt.v })}
                                    data-testid={`mount-side-${opt.v}`}
                                    className={`h-7 px-1 rounded-md border text-[10px] font-medium transition-colors ${
                                      active
                                        ? 'bg-fuchsia-900/40 border-fuchsia-500/60 text-fuchsia-200'
                                        : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                                    }`}
                                  >
                                    {opt.l}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Mount Height — 0–5 m, slider + free-form mm/m input */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] text-zinc-500">Mount Height</Label>
                              <span className="font-mono text-[10px] text-zinc-300" data-testid="mount-height-value">
                                {((selectedProjector.mount_height ?? 0.5)).toFixed(2)} m
                              </span>
                            </div>
                            <Slider
                              min={0}
                              max={5}
                              step={0.05}
                              value={[selectedProjector.mount_height ?? 0.5]}
                              onValueChange={([v]) => updateProjectorWithMulti(selected.id, { mount_height: Math.round(v * 1000) / 1000 })}
                              data-testid="mount-height-slider"
                            />
                            {/* Direct mm / m input — accepts "1500", "1500mm",
                                "1.5m", "1.5". Empty stays empty until user
                                tabs out, then we parse + clamp. */}
                            <div className="flex items-center gap-1">
                              <Input
                                type="text"
                                placeholder="e.g. 1500mm or 1.5m"
                                defaultValue={Math.round(((selectedProjector.mount_height ?? 0.5)) * 1000)}
                                key={`mh-${selected.id}-${selectedProjector.mount_height ?? 0.5}`}
                                onBlur={(e) => {
                                  const raw = String(e.target.value || '').trim().toLowerCase();
                                  if (!raw) return;
                                  // Strip unit, parse number. "1500mm"/"1500" → 1500 mm; "1.5m"/"1.5" if value < 10 → metres.
                                  const numStr = raw.replace(/[^0-9.\-]/g, '');
                                  const num = parseFloat(numStr);
                                  if (!isFinite(num)) return;
                                  const isMetres = raw.endsWith('m') && !raw.endsWith('mm') ? true
                                    : raw.endsWith('cm') ? false
                                    : raw.endsWith('mm') ? false
                                    : num <= 10; // bare number <10 → metres, otherwise mm
                                  let metres = isMetres ? num : (raw.endsWith('cm') ? num / 100 : num / 1000);
                                  metres = Math.max(0, Math.min(5, metres));
                                  updateProjectorWithMulti(selected.id, { mount_height: Math.round(metres * 1000) / 1000 });
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                className="h-7 text-xs font-mono bg-zinc-900 flex-1"
                                data-testid="mount-height-input"
                              />
                              <span className="text-[10px] text-zinc-500 font-mono">mm</span>
                            </div>
                          </div>

                          {/* L-Fold — adds a horizontal arm at the ceiling end
                              so the projector can be offset from the ceiling
                              attachment point (e.g. hang on top, project floor). */}
                          <div className="flex items-center justify-between pt-1 border-t border-zinc-800">
                            <Label className="text-xs text-zinc-400">L-Fold (horizontal arm)</Label>
                            <Switch
                              checked={!!selectedProjector.mount_lfold_enabled}
                              onCheckedChange={(val) => updateProjectorWithMulti(selected.id, { mount_lfold_enabled: val })}
                              data-testid="mount-lfold-toggle"
                            />
                          </div>

                          {selectedProjector.mount_lfold_enabled && (
                            <>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <Label className="text-[10px] text-zinc-500">L-Fold Length</Label>
                                  <span className="font-mono text-[10px] text-zinc-300" data-testid="mount-lfold-length-value">
                                    {((selectedProjector.mount_lfold_length ?? 0.5)).toFixed(2)} m
                                  </span>
                                </div>
                                <Slider
                                  min={0}
                                  max={3}
                                  step={0.05}
                                  value={[selectedProjector.mount_lfold_length ?? 0.5]}
                                  onValueChange={([v]) => updateProjectorWithMulti(selected.id, { mount_lfold_length: Math.round(v * 1000) / 1000 })}
                                  data-testid="mount-lfold-length-slider"
                                />
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="text"
                                    placeholder="e.g. 500mm or 0.5m"
                                    defaultValue={Math.round(((selectedProjector.mount_lfold_length ?? 0.5)) * 1000)}
                                    key={`mlf-${selected.id}-${selectedProjector.mount_lfold_length ?? 0.5}`}
                                    onBlur={(e) => {
                                      const raw = String(e.target.value || '').trim().toLowerCase();
                                      if (!raw) return;
                                      const numStr = raw.replace(/[^0-9.\-]/g, '');
                                      const num = parseFloat(numStr);
                                      if (!isFinite(num)) return;
                                      const isMetres = raw.endsWith('m') && !raw.endsWith('mm') ? true
                                        : raw.endsWith('cm') ? false
                                        : raw.endsWith('mm') ? false
                                        : num <= 10;
                                      let metres = isMetres ? num : (raw.endsWith('cm') ? num / 100 : num / 1000);
                                      metres = Math.max(0, Math.min(3, metres));
                                      updateProjectorWithMulti(selected.id, { mount_lfold_length: Math.round(metres * 1000) / 1000 });
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                    className="h-7 text-xs font-mono bg-zinc-900 flex-1"
                                    data-testid="mount-lfold-length-input"
                                  />
                                  <span className="text-[10px] text-zinc-500 font-mono">mm</span>
                                </div>
                              </div>
                              {/* Direction the horizontal arm extends from the
                                  vertical pipe (in projector body-local space). */}
                              <div className="space-y-1">
                                <Label className="text-[10px] text-zinc-500">L-Fold Direction</Label>
                                <div className="grid grid-cols-4 gap-1" data-testid="mount-lfold-direction">
                                  {[
                                    { v: 'forward',  l: 'Front' },
                                    { v: 'backward', l: 'Back' },
                                    { v: 'left',     l: 'Left' },
                                    { v: 'right',    l: 'Right' },
                                  ].map((opt) => {
                                    const active = (selectedProjector.mount_lfold_direction || 'forward') === opt.v;
                                    return (
                                      <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => updateProjectorWithMulti(selected.id, { mount_lfold_direction: opt.v })}
                                        data-testid={`mount-lfold-dir-${opt.v}`}
                                        className={`h-7 px-2 rounded-md border text-[10px] font-medium transition-colors ${
                                          active
                                            ? 'bg-fuchsia-900/40 border-fuchsia-500/60 text-fuchsia-200'
                                            : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                                        }`}
                                      >
                                        {opt.l}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}

                          {/* Mount Weight — feeds Total Weight + future load calc */}
                          <div className="space-y-1">
                            <Label className="text-[10px] text-zinc-500">Mount Weight (kg)</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min={0}
                              value={selectedProjector.mount_weight ?? 8}
                              onChange={(e) => updateProjectorWithMulti(selected.id, { mount_weight: parseFloat(e.target.value) || 0 })}
                              className="h-7 text-xs font-mono bg-zinc-900"
                              data-testid="mount-weight"
                            />
                          </div>
                          <p className="text-[9px] text-zinc-600 leading-tight">
                            Tip: use projector Y-rotation in Transform to swivel the mount left/right ±90°.
                          </p>
                        </div>
                      )}

                      {/* Total Weight */}
                      <div className="space-y-1 pt-1 border-t border-zinc-800">
                        <Label className="text-[10px] text-zinc-500">Total Weight (kg)</Label>
                        <div className="text-sm font-mono font-bold text-cyan-400" data-testid="total-weight">
                          {(() => {
                            const projW = selectedProjector.weight || selectedProjector.spec?.weight || 20;
                            const frameW = selectedProjector.show_frame ? (selectedProjector.frame_weight || selectedProjector.spec?.frame_weight || 0) : 0;
                            const clampW = selectedProjector.show_clamp ? (selectedProjector.clamp_weight || 2) : 0;
                            const mountW = selectedProjector.show_mount ? (selectedProjector.mount_weight ?? 8) : 0;
                            const lensW = selectedProjector.selected_lens?.weight || 0;
                            return (projW + frameW + clampW + mountW + lensW).toFixed(1);
                          })()} kg
                        </div>
                        <div className="text-[9px] text-zinc-600">
                          Projector: {selectedProjector.weight || selectedProjector.spec?.weight || 20}kg
                          {selectedProjector.selected_lens?.weight > 0 && ` + Lens: ${selectedProjector.selected_lens.weight}kg`}
                          {selectedProjector.show_frame && ` + Frame: ${selectedProjector.frame_weight || selectedProjector.spec?.frame_weight || 0}kg`}
                          {selectedProjector.show_clamp && ` + Clamp: ${selectedProjector.clamp_weight || 2}kg`}
                          {selectedProjector.show_mount && ` + Mount: ${selectedProjector.mount_weight ?? 8}kg`}
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-zinc-700" />

                    {/* Specs */}
                    <div className="space-y-2">
                      <div className="properties-section-title">Specifications</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Resolution</span>
                          <span className="font-mono">{selectedProjector.spec?.resolution || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Brightness</span>
                          <span className="font-mono">{(selectedProjector.spec?.brightness || selectedProjector.lumens || 10000).toLocaleString()} lm</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Projector Weight</span>
                          <span className="font-mono">{selectedProjector.weight || selectedProjector.spec?.weight || 20} kg</span>
                        </div>
                        {selectedProjector.selected_lens?.weight > 0 && (
                          <div className="flex justify-between">
                            <span className="text-zinc-400">Lens Weight</span>
                            <span className="font-mono">{selectedProjector.selected_lens.weight} kg</span>
                          </div>
                        )}
                        {/* PPI (Pixels Per Inch) — based on resolution and projected image width */}
                        {(() => {
                          const resW = selectedProjector.resolution_w || selectedProjector.spec?.resolution_w || 1920;
                          const throwDist = selectedProjector.throw_distance || 5;
                          const throwR = selectedProjector.throw_ratio || 1.5;
                          const imageWidthM = throwDist / throwR;
                          const imageWidthInches = imageWidthM * 39.3701;
                          const ppi = imageWidthInches > 0 ? Math.round(resW / imageWidthInches) : 0;
                          return (
                            <div className="flex justify-between">
                              <span className="text-zinc-400">PPI (Pixel/Inch)</span>
                              <span className="font-mono">{ppi}</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Lux Analysis */}
                    {(() => {
                      const projType = projectSettings?.projectType || 'indoor';
                      const luxData = getProjectorLux(selectedProjector, projType);
                      const colorStr = luxColorCSS(luxData.rating);
                      return (
                        <div className="space-y-2" data-testid="lux-analysis-section">
                          <div className="properties-section-title">Lux Analysis</div>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Lumens</span>
                              <span className="font-mono">
                                {luxData.lumens.toLocaleString()} lm
                                {luxData.stackCount > 1 && (
                                  <span className="text-cyan-400 ml-1">(×{luxData.stackCount} stack)</span>
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Projection Area</span>
                              <span className="font-mono">{luxData.area.toFixed(2)} m²</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Efficiency Factor</span>
                              <span className="font-mono">0.70</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-400">Lux Level</span>
                              <span className="font-mono font-bold" style={{ color: colorStr }}>{luxData.lux} lx</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-400">Rating ({projType})</span>
                              <span className="font-bold text-xs px-2 py-0.5 rounded" style={{ color: colorStr, backgroundColor: `rgba(${luxData.rating.color[0]},${luxData.rating.color[1]},${luxData.rating.color[2]},0.15)` }}>{luxData.rating.label}</span>
                            </div>
                            {/* Lux bar */}
                            <div className="mt-1">
                              <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((luxData.lux / (projType === 'outdoor' ? 350 : 400)) * 100, 100)}%`, backgroundColor: colorStr }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* Object properties */}
                {!isProjector && selectedObject && (
                  <>
                    <Separator className="bg-zinc-700" />

                    {/* Display Controls — hidden for LED screens (LED has its own UI), truss systems (redundant with truss configurator), and scaffolding (per UX request: edges off by default, no toggle) */}
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'truss_system' && selectedObject?.type !== 'scaffolding' && (
                      <>
                        <div className="space-y-2">
                          <div className="properties-section-title">Display</div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-zinc-400 flex items-center gap-1.5">
                              <Grid3X3 className="w-3.5 h-3.5" /> Show Edges
                            </Label>
                            <Switch
                              checked={selectedObject.show_edges !== false}
                              onCheckedChange={(val) => updateSceneObjectWithMulti(selected.id, { show_edges: val })}
                              data-testid="show-edges-toggle"
                            />
                          </div>
                        </div>

                        <Separator className="bg-zinc-700" />
                      </>
                    )}
                    
                    {/* Edit Mesh Button - hide for scaffolding, truss, and LED screens */}
                    {selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && selectedObject?.type !== 'led_screen' && (
                    <div className="space-y-2">
                      <div className="properties-section-title">Mesh Editing</div>
                      <Button
                        variant="outline"
                        className="w-full text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                        onClick={() => enterEditMode(selectedObject.id)}
                        disabled={editMode}
                        data-testid="edit-mesh-btn"
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit Vertices
                      </Button>
                      <p className="text-[10px] text-zinc-500">
                        Select, move, delete, merge vertices
                      </p>
                    </div>
                    )}

                    <Separator className="bg-zinc-700" />
                    
                    {/* LED Screen Configuration */}
                    {selectedObject?.type === 'led_screen' && selectedObject?.ledConfig && (
                      <LedScreenProperties
                        selectedObject={selectedObject}
                        updateSceneObject={updateSceneObjectWithMulti}
                        onOpenContentBrowser={() => {
                          setContentBrowserMode('led_content');
                          setContentBrowserOpen(true);
                        }}
                      />
                    )}
                    
                    {/* Dimensions with Units - edit actual size in meters (hide for LED screens, scaffolding, truss) */}
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="properties-section-title">Dimensions ({projectSettings.unit})</div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLockProportions(!lockProportions)}
                          className={`h-6 w-6 p-0 ${lockProportions ? 'text-yellow-400' : 'text-zinc-500'}`}
                          title={lockProportions ? "Unlock proportions" : "Lock proportions (scale uniformly)"}
                          data-testid="lock-proportions-btn"
                        >
                          {lockProportions ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                        </Button>
                      </div>
                      {lockProportions && (
                        <div className="text-[10px] text-yellow-400/80 bg-yellow-400/10 px-2 py-1 rounded">
                          Proportional scaling enabled
                        </div>
                      )}
                      {(() => {
                        // For imported 3D models the visible size is
                        //   dimensions (baked vertex bounds) × scale (multiplier)
                        // — show that effective size in the input boxes so the
                        // value matches what the user sees on screen and what
                        // the gizmo handles. For primitives the geometry is
                        // rebuilt from `dimensions`, so we display dims directly.
                        const _isImported = selectedObject.type === 'mesh' || selectedObject.type === 'model' || selectedObject.fileId || selectedObject.file_id;
                        const _dims = selectedObject.dimensions || { width: 1, height: 1, depth: 1 };
                        const _scale = selectedObject.scale || { x: 1, y: 1, z: 1 };
                        const _effW = _isImported ? (_dims.width || 1) * (_scale.x || 1) : (_dims.width ?? 1);
                        const _effH = _isImported ? (_dims.height || 1) * (_scale.y || 1) : (_dims.height ?? 1);
                        const _effD = _isImported ? (_dims.depth || 1) * (_scale.z || 1) : (_dims.depth ?? 1);
                        return (
                      <div className="grid grid-cols-3 gap-2">
                        <DimensionInput
                          label="Width"
                          value={_effW}
                          onChange={(meterVal) => handleDimensionChange('width', meterVal)}
                          unit={projectSettings.unit}
                          step={projectSettings.unit === 'mm' ? 10 : projectSettings.unit === 'cm' ? 1 : 0.1}
                          testId="dimension-width"
                        />
                        <DimensionInput
                          label="Height"
                          value={_effH}
                          onChange={(meterVal) => handleDimensionChange('height', meterVal)}
                          unit={projectSettings.unit}
                          step={projectSettings.unit === 'mm' ? 10 : projectSettings.unit === 'cm' ? 1 : 0.1}
                          testId="dimension-height"
                        />
                        <DimensionInput
                          label="Depth"
                          value={_effD}
                          onChange={(meterVal) => handleDimensionChange('depth', meterVal)}
                          unit={projectSettings.unit}
                          step={projectSettings.unit === 'mm' ? 10 : projectSettings.unit === 'cm' ? 1 : 0.1}
                          testId="dimension-depth"
                        />
                      </div>
                        );
                      })()}
                    </div>
                    )}

                    {/* Cylinder/Pipe Properties - Wall Thickness & Arc Angle */}
                    {(selectedObject?.type === 'cylinder' || selectedObject?.type === 'pipe') && (
                    <div className="space-y-3">
                      <div className="properties-section-title">Tube Properties</div>
                      <div className="grid grid-cols-2 gap-2">
                        <DimensionInput
                          label="Wall Thickness"
                          value={selectedObject.wallThickness || 0}
                          onChange={(meterVal) => {
                            const maxThickness = (selectedObject.dimensions?.width || 1) / 2 - 0.001;
                            const clamped = Math.min(Math.max(0, meterVal), maxThickness);
                            updateSceneObjectWithMulti(selectedObject.id, { wallThickness: clamped });
                          }}
                          unit={projectSettings.unit}
                          step={projectSettings.unit === 'mm' ? 1 : projectSettings.unit === 'cm' ? 0.1 : 0.01}
                          testId="wall-thickness"
                        />
                        <div className="space-y-1">
                          <Label className="text-[9px] text-zinc-500">Arc Angle (°)</Label>
                          <Input
                            type="number"
                            value={selectedObject.arcAngle || 360}
                            onChange={(e) => {
                              const val = Math.min(360, Math.max(1, parseFloat(e.target.value) || 360));
                              updateSceneObjectWithMulti(selectedObject.id, { arcAngle: val });
                            }}
                            min={1}
                            max={360}
                            step={1}
                            className="h-7 text-xs font-mono"
                            data-testid="arc-angle"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[9px] text-zinc-500">Top Cap</Label>
                          <Switch
                            checked={selectedObject.topCap !== false}
                            onCheckedChange={(val) => updateSceneObjectWithMulti(selectedObject.id, { topCap: val })}
                            data-testid="top-cap-toggle"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-[9px] text-zinc-500">Bottom Cap</Label>
                          <Switch
                            checked={selectedObject.bottomCap !== false}
                            onCheckedChange={(val) => updateSceneObjectWithMulti(selectedObject.id, { bottomCap: val })}
                            data-testid="bottom-cap-toggle"
                          />
                        </div>
                      </div>
                      <div className="text-[9px] text-zinc-600">
                        Wall Thickness = 0 → solid. Arc &lt; 360° → partial tube.
                      </div>
                    </div>
                    )}

                    {/* Object Resolution System - Pixel Density Control */}
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && (() => {
                      const dims = selectedObject.dimensions || { width: 1, height: 1, depth: 1 };
                      const PX_PER_METER = 300;
                      const defaultHeightRes = Math.round((dims.height || 1) * PX_PER_METER);
                      const heightRes = selectedObject.heightResolution || defaultHeightRes;
                      const pxPerMeter = heightRes / (dims.height || 1);
                      const objType = selectedObject.type?.toLowerCase() || '';

                      // Calculate per-face resolutions
                      const faces = [];
                      if (objType === 'cylinder' || objType === 'pipe') {
                        const radius = (dims.width || 1) / 2;
                        const circumference = 2 * Math.PI * radius;
                        const curvedW = Math.round(circumference * pxPerMeter);
                        const curvedH = heightRes;
                        faces.push({ name: 'Curved Surface', w: curvedW, h: curvedH });
                        const capRes = Math.round((dims.width || 1) * pxPerMeter);
                        faces.push({ name: 'Top Cap', w: capRes, h: capRes });
                        faces.push({ name: 'Bottom Cap', w: capRes, h: capRes });
                      } else if (objType === 'sphere' || objType === 'hemisphere' || objType === 'dome') {
                        const sphereW = Math.round((dims.width || 1) * pxPerMeter);
                        const sphereH = Math.round((dims.height || 1) * pxPerMeter);
                        faces.push({ name: 'Spherical Map', w: sphereW * 2, h: sphereH });
                      } else if (objType === 'plane' || objType === 'projection_screen' || objType === 'scrim_screen') {
                        const frontW = Math.round((dims.width || 1) * pxPerMeter);
                        faces.push({ name: 'Front', w: frontW, h: heightRes });
                      } else {
                        // Box-like: 6 faces
                        const wPx = Math.round((dims.width || 1) * pxPerMeter);
                        const dPx = Math.round((dims.depth || 1) * pxPerMeter);
                        faces.push({ name: 'Front', w: wPx, h: heightRes });
                        faces.push({ name: 'Back', w: wPx, h: heightRes });
                        faces.push({ name: 'Left', w: dPx, h: heightRes });
                        faces.push({ name: 'Right', w: dPx, h: heightRes });
                        faces.push({ name: 'Top', w: wPx, h: dPx });
                        faces.push({ name: 'Bottom', w: wPx, h: dPx });
                      }

                      const totalPixels = faces.reduce((sum, f) => sum + f.w * f.h, 0);

                      return (
                        <>
                          <Separator className="bg-zinc-700" />
                          <div className="space-y-3" data-testid="resolution-section">
                            <div className="properties-section-title flex items-center gap-1">
                              <Grid2X2 className="w-3 h-3" /> Resolution
                            </div>
                            <div className="space-y-2">
                              <div className="space-y-1">
                                <Label className="text-[9px] text-zinc-500">Height Resolution (px)</Label>
                                <Input
                                  type="number"
                                  value={heightRes}
                                  onChange={(e) => {
                                    const val = Math.max(1, parseInt(e.target.value) || defaultHeightRes);
                                    updateSceneObjectWithMulti(selectedObject.id, { heightResolution: val });
                                  }}
                                  min={1}
                                  step={1}
                                  className="h-7 text-xs font-mono"
                                  data-testid="height-resolution-input"
                                />
                              </div>
                              <div className="text-[9px] text-zinc-500">
                                Density: {Math.round(pxPerMeter)} px/m &nbsp;|&nbsp; Default: {PX_PER_METER} px/m
                              </div>
                            </div>

                            {/* Per-face resolution breakdown */}
                            <div className="space-y-1.5" data-testid="resolution-faces">
                              <Label className="text-[9px] text-zinc-400 font-medium">Face Resolutions</Label>
                              <div className="bg-zinc-900/60 rounded-md border border-zinc-800 p-2 space-y-1 font-mono text-[10px]">
                                {faces.map((face) => (
                                  <div key={face.name} className="flex items-center justify-between">
                                    <span className="text-zinc-500">{face.name}</span>
                                    <span className="text-zinc-300">{face.w.toLocaleString()} x {face.h.toLocaleString()} px</span>
                                  </div>
                                ))}
                                <Separator className="bg-zinc-700/50 my-1" />
                                <div className="flex items-center justify-between text-zinc-400">
                                  <span>Total</span>
                                  <span>{(totalPixels / 1000000).toFixed(2)} MP</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}

                    <Separator className="bg-zinc-700" />
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && (
                    <div className="space-y-2">
                      <div className="properties-section-title">Model Texture</div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-zinc-700 hover:border-fuchsia-500"
                        onClick={openTextureContentBrowser}
                        data-testid="select-texture-btn"
                      >
                        <FolderOpen className="w-3 h-3 mr-2" />
                        {selectedObject.textureName ? 'Change Texture' : 'Texture Upload'}
                      </Button>
                      <p className="text-[10px] text-zinc-500">
                        Upload images to the Content library first
                      </p>
                      {selectedObject.textureName && (
                        <>
                          <div className="text-xs text-zinc-400 truncate">
                            Current: {selectedObject.textureName}
                          </div>

                          {/* Universal Texture Mapping Mode */}
                          <div className="space-y-1 mt-2" data-testid="texture-mapping-mode">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-zinc-400">Mapping</span>
                              <span className="text-[10px] text-zinc-500">
                                {selectedObject.texture_mapping_mode === 'uv' && 'UV (model)'}
                                {selectedObject.texture_mapping_mode === 'triplanar' && 'Triplanar (world)'}
                                {(!selectedObject.texture_mapping_mode || selectedObject.texture_mapping_mode === 'auto') && 'Auto'}
                              </span>
                            </div>
                            <Select
                              value={selectedObject.texture_mapping_mode || 'auto'}
                              onValueChange={(v) => updateSceneObjectWithMulti(selectedObject.id, { texture_mapping_mode: v })}
                            >
                              <SelectTrigger className="h-7 text-xs bg-zinc-800 border-zinc-700" data-testid="texture-mapping-mode-trigger">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="auto" data-testid="mapping-auto">
                                  Auto <span className="text-zinc-500 text-[10px]">— UV if valid, else triplanar</span>
                                </SelectItem>
                                <SelectItem value="uv" data-testid="mapping-uv">
                                  UV Mapping <span className="text-zinc-500 text-[10px]">— use model UVs</span>
                                </SelectItem>
                                <SelectItem value="triplanar" data-testid="mapping-triplanar">
                                  Triplanar <span className="text-zinc-500 text-[10px]">— no UV needed</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {selectedObject.texture_mapping_mode === 'triplanar' && (
                              <div className="pt-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-zinc-400">Tile Scale</span>
                                  <span className="text-xs text-zinc-300">{(selectedObject.texture_mapping_scale ?? 1.0).toFixed(2)}×</span>
                                </div>
                                <Slider
                                  value={[selectedObject.texture_mapping_scale ?? 1.0]}
                                  min={0.1}
                                  max={5}
                                  step={0.1}
                                  onValueChange={([v]) => updateSceneObjectWithMulti(selectedObject.id, { texture_mapping_scale: v })}
                                  className="mt-1"
                                  data-testid="texture-mapping-scale"
                                />
                              </div>
                            )}
                          </div>

                          {/* Texture Brightness Slider */}
                          <div className="space-y-1 mt-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-zinc-400">Brightness</span>
                              <span className="text-xs text-zinc-300">{selectedObject.textureBrightness ?? 100}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="500"
                              value={selectedObject.textureBrightness ?? 100}
                              onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { textureBrightness: parseInt(e.target.value) })}
                              className="w-full h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-fuchsia-500"
                              data-testid="texture-brightness-slider"
                            />
                            <div className="flex justify-between text-[10px] text-zinc-500">
                              <span>0%</span>
                              <span>100%</span>
                              <span>500%</span>
                            </div>
                          </div>

                          {/* Texture Contrast Slider */}
                          <div className="space-y-1 mt-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-zinc-400">Contrast</span>
                              <span className="text-xs text-zinc-300">{selectedObject.textureContrast ?? 100}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="300"
                              value={selectedObject.textureContrast ?? 100}
                              onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { textureContrast: parseInt(e.target.value) })}
                              className="w-full h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                              data-testid="texture-contrast-slider"
                            />
                          </div>

                          {/* Texture Saturation Slider */}
                          <div className="space-y-1 mt-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-zinc-400">Saturation</span>
                              <span className="text-xs text-zinc-300">{selectedObject.textureSaturation ?? 100}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="300"
                              value={selectedObject.textureSaturation ?? 100}
                              onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { textureSaturation: parseInt(e.target.value) })}
                              className="w-full h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                              data-testid="texture-saturation-slider"
                            />
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-red-400 hover:text-red-300"
                            onClick={() => updateSceneObjectWithMulti(selectedObject.id, { texture: null, textureUrl: null, textureName: null, textureBrightness: 100, textureContrast: 100, textureSaturation: 100, uvMappingEnabled: false })}
                            data-testid="remove-texture-btn"
                          >
                            Remove Texture
                          </Button>
                        </>
                      )}

                      {/* Projection Mode (per-object) — Normal / Reverse / None.
                          Backward compat: legacy objects with uvMappingEnabled===true
                          and no projectionMode are treated as 'reverse' at runtime.
                          Always visible (independent of whether a texture is applied)
                          so plain-coloured objects can also be excluded with None mode. */}
                      {(() => {
                        const mode = selectedObject.projectionMode
                          || (selectedObject.uvMappingEnabled === true ? 'reverse' : 'normal');
                        const setMode = (next) => {
                          const updates = { projectionMode: next };
                          // When switching to Reverse, default UV ON so the object
                          // immediately becomes a reverse receiver.
                          if (next === 'reverse' && selectedObject.uvMappingEnabled !== true) {
                            updates.uvMappingEnabled = true;
                          }
                          updateSceneObjectWithMulti(selectedObject.id, updates);
                        };
                        const ModeBtn = ({ value, label, hint, activeClasses }) => {
                          const active = mode === value;
                          return (
                            <button
                              type="button"
                              onClick={() => setMode(value)}
                              data-testid={`projection-mode-${value}`}
                              className={`flex-1 px-2 py-1.5 rounded-md border text-left transition-colors ${
                                active
                                  ? activeClasses
                                  : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                              }`}
                            >
                              <div className="text-[11px] font-medium leading-tight">{label}</div>
                              <div className="text-[9px] opacity-80 leading-tight">{hint}</div>
                            </button>
                          );
                        };
                        return (
                          <div className="space-y-2 mt-3" data-testid="projection-mode-group">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                              Projection Mode
                            </div>
                            <div className="flex gap-1.5">
                              <ModeBtn value="normal"  label="Normal"  hint="Receives projection" activeClasses="bg-fuchsia-900/40 border-fuchsia-500/60 text-fuchsia-200" />
                              <ModeBtn value="reverse" label="Reverse" hint="UV-mapped reverse"   activeClasses="bg-amber-900/40 border-amber-500/60 text-amber-200" />
                              <ModeBtn value="none"    label="None"    hint="Texture only"       activeClasses="bg-zinc-700/60 border-zinc-500 text-zinc-100" />
                            </div>
                            {/* UV Mapping toggle — only meaningful in Reverse mode */}
                            {mode === 'reverse' && (
                              <div className={`flex items-center justify-between p-2 rounded-lg ${selectedObject.uvMappingEnabled ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-zinc-800'}`}>
                                <div className="flex items-center gap-2">
                                  <RotateCcw className={`w-3 h-3 ${selectedObject.uvMappingEnabled ? 'text-amber-400' : 'text-zinc-400'}`} />
                                  <div>
                                    <div className="text-xs font-medium">UV Mapping</div>
                                    <div className="text-[10px] text-zinc-500">
                                      On → reverse projector affects this object
                                    </div>
                                  </div>
                                </div>
                                <Switch
                                  checked={selectedObject.uvMappingEnabled ?? false}
                                  onCheckedChange={(checked) => updateSceneObjectWithMulti(selectedObject.id, { uvMappingEnabled: checked })}
                                  data-testid="uv-mapping-toggle"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    )}

                    {/* MTL Material File - hide for LED screens, scaffolding, truss */}
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && (
                    <>
                    <Separator className="bg-zinc-700" />
                    <div className="space-y-2">
                      <div className="properties-section-title">Material File (MTL)</div>
                      <p className="text-[10px] text-zinc-500">
                        Upload an .mtl file for visual material only. Projections will not apply to this object.
                      </p>
                      <input
                        type="file"
                        ref={mtlFileInputRef}
                        onChange={handleMTLUpload}
                        accept=".mtl"
                        className="hidden"
                        data-testid="mtl-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-zinc-700 hover:border-cyan-500"
                        onClick={() => mtlFileInputRef.current?.click()}
                        data-testid="mtl-upload-btn"
                      >
                        <Upload className="w-3 h-3 mr-2" />
                        {selectedObject.mtlName ? 'Change MTL File' : 'Upload MTL File'}
                      </Button>
                      {selectedObject.mtlName && (
                        <>
                          <div className="text-xs text-cyan-400 truncate" data-testid="mtl-file-name">
                            Applied: {selectedObject.mtlName}
                          </div>
                          <div className="text-[10px] text-amber-400/70">
                            Projections disabled for this object
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-red-400 hover:text-red-300"
                            onClick={handleRemoveMTL}
                            data-testid="remove-mtl-btn"
                          >
                            Remove MTL
                          </Button>
                        </>
                      )}
                    </div>
                    </>
                    )}

                    {/* Separator + UV Map - hide for LED screens, scaffolding, truss */}
                    {selectedObject?.type !== 'led_screen' && selectedObject?.type !== 'scaffolding' && selectedObject?.type !== 'truss_system' && (
                    <>
                    <Separator className="bg-zinc-700" />

                    {/* UV Map */}
                    <div className="space-y-2">
                      <div className="properties-section-title">UV Map for Projection</div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-zinc-700 hover:border-green-500"
                        onClick={handleExportUVTemplate}
                        data-testid="export-uv-template-btn"
                      >
                        <Download className="w-3 h-3 mr-2" />
                        Export UV Template for Content
                      </Button>
                      
                      {selectedObject.uvMap && (
                        <>
                          <div className="text-xs text-zinc-400 truncate">
                            Current: {selectedObject.uvMap}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs border-zinc-700 hover:border-fuchsia-500"
                              onClick={exportUVTexture}
                              data-testid="export-uv-texture-btn"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Export
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-red-400 hover:text-red-300"
                              onClick={() => updateSceneObjectWithMulti(selectedObject.id, { uvMap: null, uvMapUrl: null })}
                              data-testid="remove-uv-btn"
                            >
                              Remove
                            </Button>
                          </div>
                        </>
                      )}
                      
                      <p className="text-[10px] text-zinc-500 mt-1">
                        Create UV template → Export → Design content in Photoshop → Re-import
                      </p>
                    </div>
                    </>
                    )}

                    <Separator className="bg-zinc-700" />
                    
                    {/* Display */}
                    <div className="space-y-3">
                      <div className="properties-section-title">Display</div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Visible</Label>
                        <Switch
                          checked={selectedObject.visible}
                          onCheckedChange={(val) => updateSceneObjectWithMulti(selectedObject.id, { visible: val })}
                          data-testid="visible-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Locked
                        </Label>
                        <Switch
                          checked={selectedObject.locked}
                          onCheckedChange={(val) => updateSceneObjectWithMulti(selectedObject.id, { locked: val })}
                          data-testid="locked-toggle"
                        />
                      </div>
                      {/* Curve Degree - for plane and projection_screen */}
                      {(selectedObject.type === 'plane' || selectedObject.type === 'projection_screen') && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs text-zinc-400">Curve Degree</Label>
                            <span className="font-mono text-xs">{selectedObject.curveDegrees || 0}&deg;</span>
                          </div>
                          <Slider
                            value={[selectedObject.curveDegrees || 0]}
                            min={-360}
                            max={360}
                            step={1}
                            onValueChange={([val]) => updateSceneObjectWithMulti(selectedObject.id, { curveDegrees: val })}
                            data-testid="plane-curve-slider"
                          />
                          <p className="text-[10px] text-zinc-500">
                            + curve forward, - curve backward, 0 = flat
                          </p>
                        </div>
                      )}
                      {/* Scrim Opacity slider - only for scrim screens */}
                      {selectedObject.type === 'scrim_screen' && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <Label className="text-xs text-zinc-400">Scrim Opacity</Label>
                              <span className="font-mono text-xs">{Math.round((selectedObject.scrimOpacity || 0.15) * 100)}%</span>
                            </div>
                            <Slider
                              value={[(selectedObject.scrimOpacity || 0.15) * 100]}
                              min={1}
                              max={80}
                              step={5}
                              onValueChange={([val]) => updateSceneObjectWithMulti(selectedObject.id, { scrimOpacity: val / 100 })}
                              data-testid="scrim-opacity-slider"
                            />
                            <p className="text-[10px] text-zinc-500">
                              Lower opacity = more light passes through
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <Label className="text-xs text-zinc-400">Curve Degree</Label>
                              <span className="font-mono text-xs">{selectedObject.scrimCurveDegrees || 0}&deg;</span>
                            </div>
                            <Slider
                              value={[selectedObject.scrimCurveDegrees || 0]}
                              min={-360}
                              max={360}
                              step={1}
                              onValueChange={([val]) => updateSceneObjectWithMulti(selectedObject.id, { scrimCurveDegrees: val })}
                              data-testid="scrim-curve-slider"
                            />
                            <p className="text-[10px] text-zinc-500">
                              Positive = curve forward, Negative = curve backward, 0 = flat
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Mirror Configuration */}
                      {selectedObject.type === 'mirror' && (() => {
                        const mc = selectedObject.mirrorConfig || {};
                        const updateMirror = (updates) => {
                          updateSceneObjectWithMulti(selectedObject.id, {
                            mirrorConfig: { ...mc, ...updates }
                          });
                        };
                        return (
                          <div className="space-y-3 border-t border-zinc-700/50 pt-3" data-testid="mirror-config-section">
                            <div className="properties-section-title">Mirror</div>
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-zinc-400">Reflection</Label>
                              <Switch
                                checked={mc.reflectionEnabled || false}
                                onCheckedChange={(val) => updateMirror({ reflectionEnabled: val })}
                                data-testid="mirror-reflection-toggle"
                              />
                            </div>
                            {mc.reflectionEnabled && (
                              <>
                                <div className="space-y-1">
                                  <Label className="text-[10px] text-zinc-500">Resolution</Label>
                                  <select
                                    className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
                                    value={mc.resolution || 512}
                                    onChange={(e) => updateMirror({ resolution: parseInt(e.target.value) })}
                                    data-testid="mirror-resolution-select"
                                  >
                                    <option value={256}>256 (Low)</option>
                                    <option value={512}>512 (Medium)</option>
                                    <option value={1024}>1024 (High)</option>
                                    <option value={2048}>2048 (Ultra)</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center">
                                    <Label className="text-[10px] text-zinc-500">Reflectivity</Label>
                                    <span className="font-mono text-[10px] text-zinc-400">{Math.round((mc.reflectionLevel ?? 1) * 100)}%</span>
                                  </div>
                                  <Slider
                                    value={[(mc.reflectionLevel ?? 1) * 100]}
                                    min={10} max={100} step={5}
                                    onValueChange={([val]) => updateMirror({ reflectionLevel: val / 100 })}
                                    data-testid="mirror-reflection-level-slider"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center">
                                    <Label className="text-[10px] text-zinc-500">Blur / Roughness</Label>
                                    <span className="font-mono text-[10px] text-zinc-400">{mc.blur || 0}</span>
                                  </div>
                                  <Slider
                                    value={[mc.blur || 0]}
                                    min={0} max={32} step={1}
                                    onValueChange={([val]) => updateMirror({ blur: val })}
                                    data-testid="mirror-blur-slider"
                                  />
                                </div>

                                <div className="properties-section-title mt-2">Recursive Reflections</div>
                                <div className="flex items-center justify-between">
                                  <Label className="text-[10px] text-zinc-500">Mirror-to-Mirror</Label>
                                  <Switch
                                    checked={mc.recursionEnabled !== false}
                                    onCheckedChange={(val) => updateMirror({ recursionEnabled: val })}
                                    data-testid="mirror-recursion-toggle"
                                  />
                                </div>
                                {mc.recursionEnabled !== false && (
                                  <div className="space-y-1">
                                    <div className="flex justify-between items-center">
                                      <Label className="text-[10px] text-zinc-500">Reflection Bounces</Label>
                                      <span className="font-mono text-[10px] text-zinc-400">{mc.bounces || 3}</span>
                                    </div>
                                    <Slider
                                      value={[mc.bounces || 3]}
                                      min={1} max={10} step={1}
                                      onValueChange={([val]) => updateMirror({ bounces: val })}
                                      data-testid="mirror-bounces-slider"
                                    />
                                    <p className="text-[9px] text-zinc-600">
                                      1 = basic | 2-3 = realistic | 5+ = high realism (GPU heavy)
                                    </p>
                                  </div>
                                )}

                                <div className="space-y-1">
                                  <div className="flex justify-between items-center">
                                    <Label className="text-[10px] text-zinc-500">Max Distance (m)</Label>
                                    <span className="font-mono text-[10px] text-zinc-400">{mc.maxDistance || 100}m</span>
                                  </div>
                                  <Slider
                                    value={[mc.maxDistance || 100]}
                                    min={5} max={500} step={5}
                                    onValueChange={([val]) => updateMirror({ maxDistance: val })}
                                    data-testid="mirror-max-distance-slider"
                                  />
                                </div>
                                <p className="text-[9px] text-zinc-600">
                                  Higher resolution &amp; bounces = sharper reflection, more GPU
                                </p>
                              </>
                            )}
                          </div>
                        );
                      })()}
                      {/* Scaffolding Configuration */}
                      {selectedObject.type === 'scaffolding' && selectedObject.scaffoldConfig && (
                        <ScaffoldingProperties
                          selectedObject={selectedObject}
                          updateSceneObjectWithMulti={updateSceneObjectWithMulti}
                          activeScaffoldTab={activeScaffoldTab}
                          setActiveScaffoldTab={setActiveScaffoldTab}
                          unit={projectSettings.unit}
                          editDeckLevel={editDeckLevel}
                          setEditDeckLevel={setEditDeckLevel}
                          editDeckIndex={editDeckIndex}
                          setEditDeckIndex={setEditDeckIndex}
                          editStairLevel={editStairLevel}
                          setEditStairLevel={setEditStairLevel}
                        />
                      )}

                      {/* Truss System Configuration - Advanced */}
                      {selectedObject.type === 'truss_system' && selectedObject.trussConfig && (
                        <TrussProperties
                          selectedObject={selectedObject}
                          updateSceneObjectWithMulti={updateSceneObjectWithMulti}
                        />
                      )}

                      {/* Export Truss PDF Report - hidden for all users per
                          product decision (kept around so we can re-enable
                          later just by switching `false` to the original
                          guard if needed). */}
                      {false && selectedObject?.type === 'truss_system' && selectedObject?.trussConfig && (
                        <button
                          onClick={async () => {
                            try {
                              toast.info('Generating truss rigging report...');
                              const engine = window.__babylonEngine;
                              const scene = window.__babylonScene;
                              if (!engine || !scene) { toast.error('3D engine not ready'); return; }
                              const fileName = await exportTrussReport(scene, engine, selectedObject.trussConfig, 'Truss Project', 'User');
                              toast.success(`Report exported: ${fileName}`);
                            } catch (err) {
                              toast.error('Export failed: ' + (err.message || 'Unknown error'));
                            }
                          }}
                          className="w-full px-3 py-1.5 text-[10px] bg-cyan-600/40 hover:bg-cyan-500/40 rounded border border-cyan-500 text-cyan-200 font-semibold transition-all"
                          data-testid="truss-export-pdf"
                        >
                          Export Truss Report (PDF)
                        </button>
                      )}

                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={selectedObject.color || '#888888'}
                            onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { color: e.target.value })}
                            className="w-8 h-8 rounded cursor-pointer bg-transparent border border-zinc-700"
                            data-testid="color-picker"
                          />
                          <Input
                            value={selectedObject.color || '#888888'}
                            onChange={(e) => updateSceneObjectWithMulti(selectedObject.id, { color: e.target.value })}
                            className="h-8 text-xs font-mono"
                            data-testid="color-input"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <Layers className="w-8 h-8 mb-2 text-zinc-600" />
                <div className="text-xs text-zinc-500">Select an object to edit properties</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="multi" className="flex-1 m-0 overflow-y-auto">
          <MultiProjectionPanel />
        </TabsContent>
        
        <TabsContent value="camera" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3">
            <CameraPanel />
          </div>
        </TabsContent>
        
        <TabsContent value="settings" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3 space-y-4">
            {/* Overlap Brightness Control */}
            <div>
              <div className="properties-section-title flex items-center gap-2">
                Projection Overlap Blend
              </div>
              <div className="space-y-2 mt-3">
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-zinc-400">Overlap Brightness</Label>
                  <span className="font-mono text-xs">{projectSettings.overlapBrightness ?? 100}%</span>
                </div>
                <Slider
                  value={[projectSettings.overlapBrightness ?? 100]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={([val]) => setProjectSettings(prev => ({ ...prev, overlapBrightness: val }))}
                  data-testid="overlap-brightness-slider"
                />
                <p className="text-[10px] text-zinc-500">
                  0% = seamless blend (overlap invisible) | 100% = full additive overlap
                </p>
              </div>
            </div>

            {/* Unit Settings */}
            <div>
              <div className="properties-section-title flex items-center gap-2">
                <Ruler className="w-3 h-3" /> Measurement Units
              </div>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Display Unit</Label>
                  <Select
                    value={projectSettings.unit}
                    onValueChange={(val) => setProjectSettings(prev => ({ ...prev, unit: val }))}
                    data-testid="unit-selector"
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(UNITS).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label} ({key})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-500">
                    All measurements will display in selected unit. Internal calculations use meters.
                  </p>
                </div>
              </div>
            </div>
            
            <Separator className="bg-zinc-700" />

            {/* World Settings */}
            <div>
              <div className="properties-section-title flex items-center gap-2">
                <Layers className="w-3 h-3" /> World Settings
              </div>
              <div className="space-y-4 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Background Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={worldSettings.backgroundColor}
                      onChange={(e) => setWorldSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border border-zinc-700"
                      data-testid="world-bg-color-picker"
                    />
                    <Input
                      value={worldSettings.backgroundColor}
                      onChange={(e) => setWorldSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="h-8 text-xs font-mono"
                      data-testid="world-bg-color-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-xs text-zinc-400">Show Grid</Label>
                  <Switch
                    checked={worldSettings.showGrid}
                    onCheckedChange={(val) => setWorldSettings(prev => ({ ...prev, showGrid: val }))}
                    data-testid="world-show-grid-toggle"
                  />
                </div>

                {/* Center Axis Marker (X=red / Y=green / Z=blue) — persisted to localStorage */}
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-zinc-400">Show Center Axis</Label>
                  <Switch
                    checked={showCenterAxis}
                    onCheckedChange={(val) => {
                      setShowCenterAxis(val);
                      try { localStorage.setItem('avst_showCenterAxis', val ? 'true' : 'false'); } catch (e) {}
                      try { window.dispatchEvent(new Event('avst-center-axis-toggle')); } catch (e) {}
                    }}
                    data-testid="world-show-center-axis-toggle"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs text-zinc-400">Grid Size</Label>
                    <span className="font-mono text-xs">{formatWithUnit(worldSettings.gridSize, projectSettings.unit)}</span>
                  </div>
                  <Slider
                    value={[worldSettings.gridSize]}
                    min={50}
                    max={1000}
                    step={50}
                    onValueChange={([val]) => setWorldSettings(prev => ({ ...prev, gridSize: val }))}
                    disabled={!worldSettings.showGrid}
                    data-testid="world-grid-size-slider"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs text-zinc-400">Grid Spacing</Label>
                    <span className="font-mono text-xs">{formatWithUnit(worldSettings.gridStep || 1, projectSettings.unit)}</span>
                  </div>
                  <Slider
                    value={[worldSettings.gridStep || 1]}
                    min={0.1}
                    max={50}
                    step={0.1}
                    onValueChange={([val]) => setWorldSettings(prev => ({ ...prev, gridStep: val }))}
                    disabled={!worldSettings.showGrid}
                    data-testid="world-grid-step-slider"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Grid Line Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={worldSettings.gridLineColor}
                      onChange={(e) => setWorldSettings(prev => ({ ...prev, gridLineColor: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border border-zinc-700"
                      disabled={!worldSettings.showGrid}
                      data-testid="world-grid-line-color-picker"
                    />
                    <Input
                      value={worldSettings.gridLineColor}
                      onChange={(e) => setWorldSettings(prev => ({ ...prev, gridLineColor: e.target.value }))}
                      className="h-8 text-xs font-mono"
                      disabled={!worldSettings.showGrid}
                      data-testid="world-grid-line-color-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-zinc-700" />

            {/* Project Settings */}
            <div>
              <div className="properties-section-title flex items-center gap-2">
                <Settings className="w-3 h-3" /> Project Settings
              </div>
              <div className="space-y-4 mt-3">
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Project Type</Label>
                  <Select
                    value={projectSettings.projectType || 'indoor'}
                    onValueChange={(val) => setProjectSettings(prev => ({ ...prev, projectType: val }))}
                  >
                    <SelectTrigger className="h-8 text-xs bg-zinc-900" data-testid="project-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="indoor" className="text-xs">Indoor</SelectItem>
                      <SelectItem value="outdoor" className="text-xs">Outdoor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator className="bg-zinc-700" />

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Objects</span>
                    <span className="font-mono">{sceneObjects.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Projectors</span>
                    <span className="font-mono">{projectors.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Lens Dialog */}
      <Dialog open={addLensOpen} onOpenChange={setAddLensOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle>Add Custom Lens</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs">Model No</Label>
              <Input
                value={newLens.name}
                onChange={(e) => setNewLens({ ...newLens, name: e.target.value })}
                placeholder="e.g., UST-0.75"
                className="bg-zinc-800"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Ratio Min</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newLens.throw_ratio_min}
                  onChange={(e) => setNewLens({ ...newLens, throw_ratio_min: parseFloat(e.target.value) || 0.5 })}
                  className="bg-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Ratio Max</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newLens.throw_ratio_max}
                  onChange={(e) => setNewLens({ ...newLens, throw_ratio_max: parseFloat(e.target.value) || 2.0 })}
                  className="bg-zinc-800"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Lens Shift H (%)</Label>
                <Input
                  type="number"
                  value={newLens.lens_shift_h}
                  onChange={(e) => setNewLens({ ...newLens, lens_shift_h: parseInt(e.target.value) || 0 })}
                  className="bg-zinc-800"
                  data-testid="new-lens-shift-h"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Lens Shift V (%)</Label>
                <Input
                  type="number"
                  value={newLens.lens_shift_v}
                  onChange={(e) => setNewLens({ ...newLens, lens_shift_v: parseInt(e.target.value) || 0 })}
                  className="bg-zinc-800"
                  data-testid="new-lens-shift-v"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Lens Weight (kg)</Label>
              <Input
                type="number"
                step="0.1"
                value={newLens.weight}
                onChange={(e) => setNewLens({ ...newLens, weight: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-800"
                data-testid="new-lens-weight"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLensOpen(false)}>Cancel</Button>
            <Button onClick={handleAddLens} className="bg-fuchsia-600 hover:bg-fuchsia-500">Add Lens</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Browser Modal */}
      <ContentBrowser
        open={contentBrowserOpen}
        onClose={() => {
          setContentBrowserOpen(false);
          setContentBrowserMode(null);
        }}
        onSelect={handleContentSelect}
        title={
          contentBrowserMode === 'texture'
            ? 'Select Texture'
            : contentBrowserMode === 'led_content'
              ? 'Select LED Content'
              : 'Select Projector Media (Image or Video)'
        }
        // Object textures only accept images; projector & LED can accept both.
        filterType={contentBrowserMode === 'texture' ? 'image' : null}
      />

    </div>
    {showSmartUVExport && selectedObject && (
      <SmartUVExport
        object={selectedObject}
        scene={window.__babylonScene}
        onClose={() => setShowSmartUVExport(false)}
      />
    )}
    </>
  );
};

export default PropertiesPanel;
